// App.js — Главный файл INMONEX. Роутинг, sidebar, экраны Home/Setup/Login
// Все UI-тексты берутся из strings.js через S()
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Layers, ArrowDownCircle, BarChart3, TrendingUp, Settings, LogOut, Sun, Moon, Building2, ChevronRight, Plus, Trash2, Check, X, Eye, EyeOff, AlertCircle, ClipboardList, BookMarked, History, Crown, MessageSquare, Gift, Lock, ShieldCheck, UserCheck } from 'lucide-react';
import { ThemeCtx, THEMES, VERSION, useTheme, getCSS, uid, COLORS, ROLE_LABELS, ROLE_PERMS, Card, Btn, createOrgData, loadMeta, saveMeta, loadOrg, saveOrg, getInitialMeta, GlobalStats, CURRENCIES, setCurrencySymbol, HelpTooltip, generateOrgTheme, ORG_COLORS, fmt, normalizeOrgData, stripCyrillic, _setApiSyncRef } from './shared';
import { S, setLang, getLang } from './strings';
import { HELP_TEXTS } from './HelpText';
import * as api from './api';
import { phoneDigits, formatPhone, isValidPhone } from './phone';
import useApiSync from './useApiSync';
import { createSyncManager } from './sync';

import AFTab from './AFTab';
import OpsTab from './OpsTab';
import DebtsTab from './DebtsTab';
import TxTab from './TxTab';
import ReportsTab from './ReportsTab';
import AdminTab from './AdminTab';
import MyAccountTab from './MyAccountTab';
import DataTab from './DataTab'; // Подключили нашу новую вкладку Справочники
import HistoryTab from './HistoryTab';
import MultiOrgReportsTab from './MultiOrgReportsTab';
import SubscriptionTab from './SubscriptionTab';
import SupportTab from './SupportTab';
import ReferralTab from './ReferralTab';
import AdminPanel from './AdminPanel';
import ResetPassword from './ResetPassword';

// Авто-определение региона по таймзоне и языку браузера
function detectRegion() {
  const tz = (Intl.DateTimeFormat().resolvedOptions().timeZone || '').toLowerCase();
  const lang = (navigator.language || 'ru').toLowerCase();
  // Казахстан
  if (tz.includes('almaty') || tz.includes('aqtau') || tz.includes('aqtobe') || tz.includes('atyrau') || tz.includes('oral') || tz.includes('qostanay') || tz.includes('qyzylorda') || lang.startsWith('kk'))
    return { lang: 'kz', currency: 'KZT' };
  // СНГ страны по таймзонам
  const tzCurrMap = [
    [['moscow','samara','volgograd','yekaterinburg','omsk','novosibirsk','krasnoyarsk','irkutsk','yakutsk','vladivostok','magadan','kamchatka','kaliningrad'], 'RUB'],
    [['minsk'], 'BYN'], [['kiev','kyiv','uzhgorod','zaporozhye'], 'UAH'],
    [['baku'], 'AZN'], [['yerevan'], 'AMD'], [['tbilisi'], 'GEL'],
    [['tashkent','samarkand'], 'UZS'], [['bishkek'], 'KGS'],
    [['dushanbe'], 'TJS'], [['ashgabat'], 'TMT'], [['chisinau'], 'MDL'],
  ];
  for (const [zones, cur] of tzCurrMap) {
    if (zones.some(z => tz.includes(z))) return { lang: 'ru', currency: cur };
  }
  if (lang.startsWith('ru') || lang.startsWith('uk') || lang.startsWith('be')) return { lang: 'ru', currency: 'RUB' };
  return { lang: 'en', currency: 'USD' };
}

// === РЕФЕРАЛЬНЫЙ КОД: чтение из localStorage ===
function getStoredReferralCode() {
  try {
    const raw = localStorage.getItem('inmonex_ref');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.code || !parsed.expiresAt) return null;
    if (Date.now() > parsed.expiresAt) {
      localStorage.removeItem('inmonex_ref');
      return null;
    }
    return parsed.code;
  } catch (e) {
    return null;
  }
}
function clearStoredReferralCode() {
  try { localStorage.removeItem('inmonex_ref'); } catch (e) {}
}

// Если URL содержит /admin — рендерим админ-панель вместо приложения
const isAdminRoute = window.location.pathname.includes('/admin');
// Сброс пароля: /app/?token=XXX#/reset-password
const isResetPasswordRoute =
  window.location.hash.startsWith('#/reset-password') &&
  new URLSearchParams(window.location.search).has('token');

export default function App() {
  // Сброс пароля по ссылке из email — отдельный экран
  if (isResetPasswordRoute) return <ResetPassword />;
  // Админ-панель — отдельная SPA
  if (isAdminRoute) return <AdminPanel />;
  const [meta, setMeta] = useState(() => {
    const m = getInitialMeta();
    // Инициализация языка синхронно ДО первого рендера
    if (m.lang) {
      setLang(m.lang);
    } else {
      const detected = detectRegion();
      setLang(detected.lang);
      m.lang = detected.lang;
      m.detectedCurrency = detected.currency;
    }
    return m;
  });
  const [orgId, setOrgId] = useState(null);
  const [orgData, setOrgData] = useState(null);
  // Phase 9.3: timestamp последнего успешного контакта с сервером (любой successful
  // GET /version или PUT /sync/org). Используется для индикатора «Обновлено N сек назад».
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  // Phase A (2026-05-26): outbox удалён (см. shared.js header). One-shot cleanup
  // orphan localStorage от Task #54/#48 — после первого mount можно удалить этот блок.
  React.useEffect(() => {
    try { localStorage.removeItem('inmonex_sync_outbox_v1'); } catch {}
  }, []);
  // Stage 20a Phase 2 / Чанк 20a.4a (2026-05-13): funds читаются из нормализованной
  // таблицы Fund через GET /api/orgs/:orgId/funds. Это balances из БД (атомарно
  // обновляемые backend в Чанке 20a.3 при операциях). Metadata (name/percent/color/
  // archived) пока продолжает идти из syncData.funds JSON — переключим в 20a.4b.
  // apiFunds=null означает «ещё не загружено» — UI работает на JSON как fallback.
  const [apiFunds, setApiFunds] = useState(null);
  // 20a.4a fix: triggers refetch funds на successful saveOrg или poll-update,
  // НЕ на каждом 30с onCheck (избегаем лишних запросов).
  const [fundsRefetchToken, setFundsRefetchToken] = useState(0);
  // 20a.4a fix BLOCKING #1: tracking «грязных» (только что отредактированных через uOrg)
  // fund balances. До успешного saveOrg + refetch apiFunds — UI показывает local optimistic
  // значение, не API. Иначе пользователь видит флик старого значения на ~1.1с после операции.
  const dirtyFundsRef = useRef(new Map()); // fundId → editedAt timestamp
  const prevFundBalancesRef = useRef(null); // для сравнения предыдущего state с новым
  // 20a.4a fix HIGH #4: immediate fetch при первой загрузке орг, debounce только для повторных
  const apiFundsFirstFetchDoneRef = useRef(false);
  // Stage 20a Phase 2 / Чанк 20a.6a (2026-05-14): тот же паттерн что для funds (20a.4a)
  // применяется к accounts. apiAccounts.balance — authoritative из БД (Чанк 2C+20a.3
  // атомарно обновляет Account.balance при операциях). UI читает data.accountBalances[id]
  // через view-layer overlay вместо JSON syncData.accountBalances (legacy).
  // dirty tracking даёт optimistic UX — после uOrg state локально показывает new balance,
  // overlay не override'ит до refetch apiAccounts с правильными data из БД.
  const [apiAccounts, setApiAccounts] = useState(null);
  const [accountsRefetchToken, setAccountsRefetchToken] = useState(0);
  const dirtyAccountsRef = useRef(new Map()); // accountId → editedAt timestamp
  const prevAccountBalancesRef = useRef(null);
  const apiAccountsFirstFetchDoneRef = useRef(false);
  // Stage 21.4 (2026-05-14): apiAuditLog view-layer overlay для миграции auditLog
  // из JSON syncData.auditLog в нормализованную AuditLog таблицу. UI HistoryTab
  // читает data.auditLog (через orgDataView overlay) — теперь оно из REST.
  // apiAuditLog=null = ещё не загружено, UI показывает state.auditLog как fallback.
  const [apiAuditLog, setApiAuditLog] = useState(null);
  const [auditLogRefetchToken, setAuditLogRefetchToken] = useState(0);
  const apiAuditLogFirstFetchDoneRef = useRef(false);
  // Stage 20b Phase 2 / Чанк B.2 (2026-05-14): apiCategories view-layer overlay.
  const [apiCategories, setApiCategories] = useState(null);
  const [categoriesRefetchToken, setCategoriesRefetchToken] = useState(0);
  const apiCategoriesFirstFetchDoneRef = useRef(false);
  // Phase 10: timestamp последнего 409 VERSION_CONFLICT (или null). Используется
  // для показа toast «Данные обновлены другим пользователем». Авто-скрывается через 5 сек.
  const [syncConflictToast, setSyncConflictToast] = useState(null);
  const [globalUser, setGlobalUser] = useState(null);
  // Hash-роутинг: /#/register → сразу визард регистрации
  const [screen, setScreen] = useState(() => {
    if (window.location.hash === '#/register') return 'setup';
    return 'home';
  });
  const [, forceUpdate] = useState(0); // для перерендера при смене языка

  // Синхронизация screen ↔ hash
  useEffect(() => {
    const onHash = () => {
      if (window.location.hash === '#/register' && screen !== 'setup') setScreen('setup');
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [screen]);

  // Обновляем hash при смене экрана
  const setScreenWithHash = useCallback((s) => {
    if (s === 'setup' && !globalUser) {
      window.history.replaceState(null, '', '#/register');
    } else if (window.location.hash === '#/register') {
      window.history.replaceState(null, '', window.location.pathname);
    }
    setScreen(s);
  }, [globalUser]);

  // Функция смены языка (вызывается из AdminTab)
  const changeLang = (lang) => {
    setLang(lang);
    setMeta(p => ({ ...p, lang }));
    forceUpdate(n => n + 1);
  };

  // ─── Stage 4.0 / Phase 9.1: polling manager ref ──────────────────────
  // Объявлен раньше useEffect saveOrg, потому что callback внутри saveOrg
  // ссылается на syncManagerRef (Phase 9.1.5: setVersion после PUT).
  const syncManagerRef = useRef(null);
  // Отдельный ref на orgId — чтобы effect ниже зависел только от смены orgId,
  // не от каждого setOrgData (иначе manager перезапускается на каждый poll-update).
  const lastStartedOrgIdRef = useRef(null);

  useEffect(() => { saveMeta(meta) }, [meta]);
  useEffect(() => {
    if (!orgId || !orgData) return;
    // Защита: проверяем что orgData.name совпадает с именем орг в meta
    const orgMeta = (meta.orgs || []).find(o => o.id === orgId);
    if (orgMeta && orgData.name && orgMeta.name !== orgData.name) {
      console.warn('[APP] Cross-contamination prevented! orgId:', orgId, 'orgMeta.name:', orgMeta.name, 'orgData.name:', orgData.name);
      return; // НЕ сохраняем — данные от другой орг
    }
    // Phase 9.1.5: после удачного PUT — синхронизуем lastKnownVersion в polling manager.
    // Иначе следующий poll увидит updatedAt server > lastKnown → лишний full refetch →
    // setOrgData → этот же useEffect → ещё PUT → loop. setVersion разрывает цикл.
    // Phase 10: expectedSyncedAt берём из manager-а — там самая свежая версия
    // (обновляется после каждого PUT и poll). orgData._syncedAt не годится — он
    // stale после первого save (мы не обновляем _syncedAt в state чтобы не
    // триггерить рекурсивный useEffect).
    const expectedSyncedAt =
      (syncManagerRef.current && syncManagerRef.current.getVersion && syncManagerRef.current.getVersion())
      || orgData?._syncedAt
      || null;
    saveOrg(orgId, orgData, (result) => {
      // Phase 10: VERSION_CONFLICT — другой юзер успел сохранить раньше.
      // Применяем свежие данные с сервера, обновляем version в polling manager,
      // показываем toast чтобы юзер знал что произошло.
      if (result?.conflict && result?.current) {
        try {
          const normalized = normalizeOrgData(result.current);
          if (result.updatedAt) normalized._syncedAt = result.updatedAt;
          // Чанк 20a.4b B1 fix: backend больше не пишет fundBalances в JSON.
          // Восстанавливаем balances из apiFunds (если уже подгружены) чтобы
          // последующие uOrg в дочках не работали от пустого fundBalances.
          setOrgData(restoreFundBalancesFromApi(normalized));
          if (syncManagerRef.current && result.updatedAt) {
            syncManagerRef.current.setVersion(result.updatedAt);
          }
          setLastSyncedAt(Date.now());
          // 20a.4a: после 409 conflict resolution данные изменились — refetch funds
          setFundsRefetchToken(n => n + 1);
          // 20a.6a: и accounts тоже — данные могли измениться.
          setAccountsRefetchToken(n => n + 1);
          // Stage 21.4: и auditLog (новые entries от других писателей).
          setAuditLogRefetchToken(n => n + 1);
          // Stage 20b B.2: и categories (могли измениться).
          setCategoriesRefetchToken(n => n + 1);
          // Stage 20 follow-up + Stage 21.4 fix (2026-05-14): detect race-with-own-op.
          // Если recent REST write был <3s назад — backend POST/PUT/DELETE touch'нул
          // Org.updatedAt и наш saveOrg PUT прилетел с stale expectedSyncedAt.
          // Resolve correct. НЕ показываем toast (это не реальный конфликт от другого
          // юзера, а наш собственный race) и log как debug. Реальные конфликты
          // (без recent own write) → toast + warn.
          const recentOwnWrite = (Date.now() - api.getLastRestWriteAt()) < 3000;
          if (recentOwnWrite) {
            console.debug('[SYNC] 409 race-with-own-op — applied server data (no toast)');
          } else {
            setSyncConflictToast(Date.now());
            console.warn('[SYNC] 409 VERSION_CONFLICT — applied server data');
          }
        } catch (e) {
          console.error('[SYNC] failed to apply conflict resolution:', e);
        }
        return;
      }
      if (result?.updatedAt && syncManagerRef.current) {
        syncManagerRef.current.setVersion(result.updatedAt);
      }
      // Phase 9.3: успешный PUT — обновляем индикатор синхронизации
      if (result?.updatedAt) {
        setLastSyncedAt(Date.now());
        // 20a.4a: триггер refetch funds — backend применил наши изменения,
        // нужно подтянуть актуальный Fund.balance из БД.
        setFundsRefetchToken(n => n + 1);
        // 20a.6a: и accounts тоже.
        setAccountsRefetchToken(n => n + 1);
        // Stage 21.4: и auditLog (backend write через writeAuditEntry).
        setAuditLogRefetchToken(n => n + 1);
        // Stage 20b B.2: и categories.
        setCategoriesRefetchToken(n => n + 1);
      }
    }, expectedSyncedAt);
  }, [orgId, orgData, meta.orgs]);

  const uMeta = useCallback(fn => setMeta(p => typeof fn === 'function' ? fn(p) : { ...p, ...fn }), []);
  const uOrg = useCallback(fn => setOrgData(p => typeof fn === 'function' ? fn(p) : { ...p, ...fn }), []);
  const toggle = () => uMeta(p => ({ ...p, theme: p.theme === 'dark' ? 'light' : 'dark' }));

  // ─── Stage 4.0 / Phase 9.1: real-time polling ────────────────────────
  // Каждые 30 сек проверяем GET /api/sync/org/:orgId/version. Если updatedAt
  // изменился — затягиваем full GET и обновляем orgData. Дополнительно реагируем
  // на visibilitychange (Alt+Tab возврат) и triggerCheck (вызывается из MainApp
  // при смене вкладки в sidebar).
  // syncManagerRef и lastStartedOrgIdRef объявлены выше (рядом с useEffect saveOrg)
  // для доступа из callback Phase 9.1.5.

  useEffect(() => {
    const inApp = orgId && orgData && api.getToken();
    if (!inApp) {
      // Outside app screen — stop polling
      if (syncManagerRef.current) {
        syncManagerRef.current.stop();
        syncManagerRef.current = null;
        lastStartedOrgIdRef.current = null;
      }
      setLastSyncedAt(null); // Phase 9.3: вышли из орг — сбрасываем индикатор
      return;
    }
    // Если уже polling запущен для этой orgId — ничего не делаем
    if (syncManagerRef.current && lastStartedOrgIdRef.current === orgId) return;
    // Иначе — стопим старый и запускаем новый
    if (syncManagerRef.current) syncManagerRef.current.stop();
    // Phase 9.3: при первом старте polling-а для орг — стартуем с now() как точку отсчёта
    // (orgData только что загружена с сервера, значит «just synced»)
    setLastSyncedAt(Date.now());
    const manager = createSyncManager({
      orgId,
      initialSyncedAt: orgData?._syncedAt || null,
      onUpdate: (fresh, info) => {
        // P1.3.3 (2026-05-21): защита от перетирания in-flight POST.
        // Корневая причина 7 ZOMBIE_DUP в ИП ЕлемесовЛаб: юзер кликает «Сохранить долг»,
        // optimistic update добавляет долг в local state, apiSync.syncCreateDebt запускает
        // POST в фоне. Пока POST идёт 1-3 сек — polling tick может прилететь и заменить
        // orgData свежими данными с сервера БЕЗ только что созданного долга (он ещё не в БД).
        // Юзер думает «не сохранилось» → пересоздаёт → ZOMBIE_DUP.
        //
        // Используем тот же tracker (_lastRestWriteAt) что и 409 conflict resolution выше.
        // Threshold 5000ms (больше чем 3000 для 409): polling speculative, in-flight POST
        // ещё работает, нужен больший запас. После 5 сек polling сработает на следующем
        // тике (через 30 сек polling cycle).
        const recentWriteMs = Date.now() - api.getLastRestWriteAt();
        if (recentWriteMs < 5000) {
          console.debug(`[POLL] skip refresh — recent REST write ${recentWriteMs}ms ago (защита от перетирания in-flight POST)`);
          return;
        }
        try {
          const normalized = normalizeOrgData(fresh);
          // Сохраняем _syncedAt чтобы manager.setVersion корректно работал в будущем
          if (fresh._syncedAt) normalized._syncedAt = fresh._syncedAt;
          // Чанк 20a.4b B1 fix: backend не пишет fundBalances в JSON →
          // восстанавливаем из apiFunds (last known) перед setOrgData.
          setOrgData(restoreFundBalancesFromApi(normalized));
          // 20a.4a: poll подтянул новые данные орг — refetch funds тоже
          // (например другой юзер сделал операцию).
          setFundsRefetchToken(n => n + 1);
          // 20a.6a: и accounts тоже.
          setAccountsRefetchToken(n => n + 1);
          // Stage 21.4: и auditLog (другой юзер мог что-то сделать).
          setAuditLogRefetchToken(n => n + 1);
          // Stage 20b B.2: и categories тоже.
          setCategoriesRefetchToken(n => n + 1);
          console.log(`[POLL] ${info?.reason || 'update'} → orgData refreshed (syncedAt=${fresh._syncedAt})`);
        } catch (e) {
          console.warn('[POLL] failed to apply update:', e?.message);
        }
      },
      onError: (err, info) => {
        // Тихая обработка — сервер может быть временно недоступен
        if (err?.status !== 401 && err?.status !== 403) {
          console.warn(`[POLL] ${info?.reason || 'check'} error:`, err?.message);
        }
      },
      // Phase 9.3: успешный GET /version (любой reason) — обновляем индикатор
      onCheck: () => setLastSyncedAt(Date.now()),
    });
    manager.start();
    syncManagerRef.current = manager;
    lastStartedOrgIdRef.current = orgId;
    return () => {
      // unmount только при изменении orgId/screen — НЕ на каждый orgData update
    };
  }, [orgId, !!orgData, !!api.getToken()]);

  // Phase A (2026-05-26): drain logic удалён. handleSyncError в useApiSync.js
  // alert'ит юзера на ЛЮБОЙ ошибке (network/5xx/4xx) — без silent retry.
  // Phase B (TODO): callsite refactor на await+rollback uOrg при ошибке.

  // Task #50 (2026-05-25): listener на org-version-updated — useApiSync шлёт это
  // событие после успешного POST/PUT/DELETE /operations с _orgUpdatedAt из response.
  // Здесь обновляем syncManager.lastKnownVersion чтобы следующий saveOrg прилетел
  // со свежим _expectedSyncedAt → меньше 409 race-with-own-op в console.
  React.useEffect(() => {
    const onVersionUpdate = (e) => {
      const { orgId: evOrgId, updatedAt } = e.detail || {};
      // Безопасность: применяем только если событие для текущей орг.
      if (!evOrgId || evOrgId !== orgId) return;
      if (!updatedAt) return;
      try {
        if (syncManagerRef.current && syncManagerRef.current.setVersion) {
          syncManagerRef.current.setVersion(updatedAt);
        }
        setLastSyncedAt(Date.now());
      } catch (err) {
        console.warn('[org-version-updated] failed:', err?.message);
      }
    };
    window.addEventListener('org-version-updated', onVersionUpdate);
    return () => window.removeEventListener('org-version-updated', onVersionUpdate);
  }, [orgId]);

  // F2 part4 (2026-06-08): listener inmonex-refetch-funds — MainApp (рендер AFTab) НЕ имеет
  // setFundsRefetchToken в scope (он в App). FundManagerModal после sequential-save шлёт это
  // событие → здесь дёргаем refetch фондов+счетов из БД (single-source отображение).
  React.useEffect(() => {
    const onRefetchFunds = () => {
      try {
        setFundsRefetchToken(n => n + 1);
        setAccountsRefetchToken(n => n + 1);
      } catch (err) {
        console.warn('[inmonex-refetch-funds] failed:', err?.message);
      }
    };
    window.addEventListener('inmonex-refetch-funds', onRefetchFunds);
    return () => window.removeEventListener('inmonex-refetch-funds', onRefetchFunds);
  }, []);

  // Stage 20g (2026-06-08): мягкая перезагрузка данных орг (БЕЗ full page reload, чтобы не
  // сбросить на выбор организации). После отмены оплаты долга обновляет операции/долги/балансы
  // из БД. Форсированный аналог polling onUpdate.
  React.useEffect(() => {
    const onReloadOrg = async () => {
      if (!orgId || !api.getToken()) return;
      try {
        const fresh = await api.loadOrgFromServer(orgId);
        if (!fresh || !fresh.name) return;
        const normalized = normalizeOrgData(fresh);
        if (fresh._syncedAt) {
          normalized._syncedAt = fresh._syncedAt;
          if (syncManagerRef.current && syncManagerRef.current.setVersion) syncManagerRef.current.setVersion(fresh._syncedAt);
        }
        setOrgData(restoreFundBalancesFromApi(normalized));
        setFundsRefetchToken(n => n + 1);
        setAccountsRefetchToken(n => n + 1);
        setAuditLogRefetchToken(n => n + 1);
        setCategoriesRefetchToken(n => n + 1);
        setLastSyncedAt(Date.now());
      } catch (e) {
        console.warn('[inmonex-reload-org] failed:', e?.message);
      }
    };
    window.addEventListener('inmonex-reload-org', onReloadOrg);
    return () => window.removeEventListener('inmonex-reload-org', onReloadOrg);
  }, [orgId]);

  // Stage 20h (2026-06-09): refetch категорий после commit (useApiSync category dispatch).
  // merge берёт метаданные категорий из apiCategories — иначе имя/привязка stale до poll.
  React.useEffect(() => {
    const onRefetchCategories = () => {
      try { setCategoriesRefetchToken(n => n + 1); } catch (err) {}
    };
    window.addEventListener('inmonex-refetch-categories', onRefetchCategories);
    return () => window.removeEventListener('inmonex-refetch-categories', onRefetchCategories);
  }, []);

  // Helper для on-demand triggers (передаётся в MainApp)
  const triggerSyncCheck = useCallback((reason) => {
    if (syncManagerRef.current) syncManagerRef.current.triggerCheck(reason);
  }, []);

  // Чанк 20a.4b B1 + Hotfix 20a.6a.1 + Чанк 20a.6b: восстановить fundBalances + funds metadata + accountBalances
  // из apiFunds/apiAccounts перед setOrgData. После cut backend больше не пишет это в JSON
  // (всегда {} или [] в response). Без restore uOrg callbacks в дочках делают
  // `nA = {...p.accountBalances}` / `nF = {...p.fundBalances}` от пустого объекта
  // и распределяют операции «от нуля», а не от текущего баланса → drift.
  //
  // Чанк 20a.6b (2026-05-14): расширили на accountBalances. Симметрично с funds в 20a.6a.1:
  // после cut accountBalances из JSON, state.accountBalances={} при polling/conflict resolve →
  // POST /operations через apiSync.syncCreateOperation отправляет txData с правильным amount
  // (это работает, backend applyAccountBalanceDelta). НО локальный uOrg делает
  // optimistic update `nA[accId] = (p.accountBalances[accId] || 0) + sum` → если
  // p.accountBalances пуст, optimistic UI показывает amount вместо total — на 1 кадр
  // юзер видит «баланс = только что добавленная сумма» вместо «было + новое».
  // После refetch apiAccounts (через ~600ms) view-layer overlay показывает правильное.
  // Restore закрывает этот gap — state.accountBalances populates из apiAccounts.
  const restoreFromApi = useCallback((normalizedData) => {
    if (!normalizedData) return normalizedData;
    let result = normalizedData;

    // Funds: restore fundBalances + funds metadata array
    if (Array.isArray(apiFunds) && apiFunds.length > 0) {
      const restoredFundBalances = { ...(result.fundBalances || {}) };
      for (const f of apiFunds) {
        if (f && typeof f.id === 'string') {
          const balNum = Number(f.balance);
          if (Number.isFinite(balNum)) restoredFundBalances[f.id] = balNum;
        }
      }
      const restoredFunds = apiFunds.map(f => ({
        id: f.id,
        name: typeof f.name === 'string' ? f.name : '',
        percent: Number.isFinite(Number(f.percent)) ? Number(f.percent) : 0,
        color: f.color || null,
        archived: !!f.isArchived,
      }));
      result = { ...result, fundBalances: restoredFundBalances, funds: restoredFunds };
    }

    // Accounts: restore accountBalances map. Account list уже приходит через smart-merge
    // (backend buildOrgDataFromTables возвращает accounts с balance field), но
    // accountBalances map отдельно и был frozen после 20a.6b cut.
    if (Array.isArray(apiAccounts) && apiAccounts.length > 0) {
      const restoredAccBalances = { ...(result.accountBalances || {}) };
      for (const a of apiAccounts) {
        if (a && typeof a.id === 'string') {
          const balNum = Number(a.balance);
          if (Number.isFinite(balNum)) restoredAccBalances[a.id] = balNum;
        }
      }
      result = { ...result, accountBalances: restoredAccBalances };
    }

    return result;
  }, [apiFunds, apiAccounts]);

  // Backward-compat alias для callsites которые ожидают старое имя.
  const restoreFundBalancesFromApi = restoreFromApi;

  // 🚨 Hotfix 20a.6a.1 + 20a.6b: initial load gap. setOrgData в loadOrgFromServer
  // и в switchOrg/openOrg вызывается ДО того как apiFunds/apiAccounts успевают
  // загрузиться. В тот момент state.funds=[]+state.accountBalances={} (backend cuts).
  // Если юзер моментально создаст operation — distribution math ломается → drift.
  // Этот useEffect re-restore'ит state сразу после первой загрузки api*.
  //
  // FIX (external review): guard должен быть per-side — НЕ требовать stateHasFunds
  // если apiFunds пуст (legitimate org с 0 funds). Иначе useEffect бесконечно
  // перезапускался бы т.к. restoreFromApi возвращает новый object reference.
  // Restore нужен ТОЛЬКО когда apiSide данных больше чем в state.
  useEffect(() => {
    if (!orgData) return;
    const fundsReady = Array.isArray(apiFunds) && apiFunds.length > 0;
    const accountsReady = Array.isArray(apiAccounts) && apiAccounts.length > 0;
    if (!fundsReady && !accountsReady) return;
    const stateHasFunds = Array.isArray(orgData.funds) && orgData.funds.length > 0;
    const stateHasAccBal = orgData.accountBalances && Object.keys(orgData.accountBalances).length > 0;
    // Restore нужен только по той стороне, где API имеет данные, а state — нет.
    const needFundsRestore = fundsReady && !stateHasFunds;
    const needAccBalRestore = accountsReady && !stateHasAccBal;
    if (!needFundsRestore && !needAccBalRestore) return;
    setOrgData(p => p ? restoreFromApi(p) : p);
  }, [apiFunds, apiAccounts, orgData, restoreFromApi]);

  // ─── Stage 20a Phase 2 / Чанк 20a.4a: refetch Fund balances from API ──────
  // Triggers через fundsRefetchToken — увеличивается в saveOrg success и polling
  // onUpdate (НЕ в onCheck). Это избегает 30с-spam запросов когда на сервере
  // ничего не менялось. Backend в Чанке 20a.3 атомарно обновляет Fund.balance
  // при POST/PUT/DELETE /operations — нам нужен fresh fetch после каждого write.
  //
  // Immediate fetch при первой загрузке орг (UI ждёт API balances для отображения).
  // Debounce 600ms для повторных fetches (даёт saveOrg flush + backend processing).
  useEffect(() => {
    if (!orgId || !orgData) {
      setApiFunds(null);
      apiFundsFirstFetchDoneRef.current = false;
      dirtyFundsRef.current.clear();
      prevFundBalancesRef.current = null;
      return;
    }
    let cancelled = false;
    const fetchNow = () => {
      if (cancelled) return;
      api.getFunds(orgId, { includeArchived: true })
        .then(result => {
          if (cancelled) return;
          if (Array.isArray(result?.funds)) {
            setApiFunds(result.funds);
            // 20a.4a fix BLOCKING #1: получили свежие данные из БД — очищаем
            // dirty для всех fund-ов которых backend знает. UI больше не нуждается
            // в optimistic overlay для них.
            for (const f of result.funds) {
              if (f && typeof f.id === 'string') {
                dirtyFundsRef.current.delete(f.id);
              }
            }
          }
        })
        .catch(e => {
          if (!cancelled) {
            console.warn('[FUNDS API] fetch failed, UI uses JSON funds:', e?.message);
          }
        });
    };
    if (!apiFundsFirstFetchDoneRef.current) {
      apiFundsFirstFetchDoneRef.current = true;
      fetchNow();
      return () => { cancelled = true; };
    }
    const id = setTimeout(fetchNow, 600);
    return () => { cancelled = true; clearTimeout(id); };
  }, [orgId, !!orgData, fundsRefetchToken]);

  // Stage 20a Phase 2 / Чанк 20a.6a: тот же паттерн для apiAccounts — fetch при
  // саlвешном PUT или poll-update. Account.balance из БД authoritative (Чанк 2C+20a.3).
  useEffect(() => {
    if (!orgId || !orgData) {
      setApiAccounts(null);
      apiAccountsFirstFetchDoneRef.current = false;
      dirtyAccountsRef.current.clear();
      prevAccountBalancesRef.current = null;
      return;
    }
    let cancelled = false;
    const fetchNow = () => {
      if (cancelled) return;
      api.getAccounts(orgId)
        .then(result => {
          if (cancelled) return;
          if (Array.isArray(result)) {
            setApiAccounts(result);
            // Чанк 20a.6a: cleanup dirty для всех accounts которых backend знает.
            for (const a of result) {
              if (a && typeof a.id === 'string') {
                dirtyAccountsRef.current.delete(a.id);
              }
            }
          }
        })
        .catch(e => {
          if (!cancelled) {
            console.warn('[ACCOUNTS API] fetch failed, UI uses JSON accountBalances:', e?.message);
          }
        });
    };
    if (!apiAccountsFirstFetchDoneRef.current) {
      apiAccountsFirstFetchDoneRef.current = true;
      fetchNow();
      return () => { cancelled = true; };
    }
    const id = setTimeout(fetchNow, 600);
    return () => { cancelled = true; clearTimeout(id); };
  }, [orgId, !!orgData, accountsRefetchToken]);

  // Stage 21.4 (2026-05-14): fetch audit log из AuditLog таблицы через REST.
  // UI HistoryTab читает data.auditLog (через orgDataView overlay) — теперь оно
  // из БД, не из JSON syncData.auditLog. Триггеры: первая загрузка орг + saveOrg
  // success + polling onUpdate (auditLogRefetchToken bump).
  useEffect(() => {
    if (!orgId || !orgData) {
      setApiAuditLog(null);
      apiAuditLogFirstFetchDoneRef.current = false;
      return;
    }
    let cancelled = false;
    const fetchNow = () => {
      if (cancelled) return;
      api.getAuditLog(orgId, { shape: 'ui', limit: 1000 })
        .then(result => {
          if (cancelled) return;
          if (result && Array.isArray(result.logs)) {
            setApiAuditLog(result.logs);
          }
        })
        .catch(e => {
          if (!cancelled) {
            console.warn('[AUDIT API] fetch failed, UI uses JSON auditLog:', e?.message);
          }
        });
    };
    if (!apiAuditLogFirstFetchDoneRef.current) {
      apiAuditLogFirstFetchDoneRef.current = true;
      fetchNow();
      return () => { cancelled = true; };
    }
    const id = setTimeout(fetchNow, 600);
    return () => { cancelled = true; clearTimeout(id); };
  }, [orgId, !!orgData, auditLogRefetchToken]);

  // Stage 20b Phase 2 / Чанк B.2 (2026-05-14): fetch categories из БД.
  useEffect(() => {
    if (!orgId || !orgData) {
      setApiCategories(null);
      apiCategoriesFirstFetchDoneRef.current = false;
      return;
    }
    let cancelled = false;
    const fetchNow = () => {
      if (cancelled) return;
      api.getCategories(orgId, { includeArchived: true })
        .then(result => {
          if (cancelled) return;
          if (result && Array.isArray(result.categories)) {
            setApiCategories(result.categories);
          }
        })
        .catch(e => {
          if (!cancelled) {
            console.warn('[CATEGORIES API] fetch failed, UI uses JSON:', e?.message);
          }
        });
    };
    if (!apiCategoriesFirstFetchDoneRef.current) {
      apiCategoriesFirstFetchDoneRef.current = true;
      fetchNow();
      return () => { cancelled = true; };
    }
    const id = setTimeout(fetchNow, 600);
    return () => { cancelled = true; clearTimeout(id); };
  }, [orgId, !!orgData, categoriesRefetchToken]);

  // View layer: overlay API fund balances над JSON. Дочерние компоненты получают
  // `data.fundBalances[fundId]` уже из БД (через backend Чанка 20a.3) вместо
  // потенциально stale JSON. funds metadata остаётся из JSON (Чанк 20a.4b).
  //
  // SYNCHRONOUS dirty detection: сравниваем orgData.fundBalances с prevRef ВНУТРИ
  // useMemo (синхронно перед merge), а не в useEffect (который выполняется после
  // render → флик старого balance на 1 кадр после uOrg). Mutation refs внутри
  // useMemo допустима — она не trigger re-render.
  const orgDataView = useMemo(() => {
    if (!orgData) {
      prevFundBalancesRef.current = null;
      prevAccountBalancesRef.current = null;
      return orgData;
    }
    const nextBalances = orgData.fundBalances || {};
    const prevBalances = prevFundBalancesRef.current;
    // Synchronous dirty detection
    // Чанк A.1 (2026-05-15): после Stage 20a Phase 2 server вырезает fundBalances
    // из payload bulk PUT /sync/org → polling возвращает orgData.fundBalances={}.
    // Если prevBalances непустой (был optimistic update / старый localStorage кэш),
    // переход X→undefined ОШИБОЧНО помечался как dirty → overlay из apiFunds skip'ал
    // → UI показывал 0,00 ₸ на фондах. Skip dirty mark когда nextBalances={} —
    // это архитектурный переход на нормализованную таблицу, не локальная правка.
    if (prevBalances !== null) {
      const nextIsEmpty = Object.keys(nextBalances).length === 0;
      if (!nextIsEmpty) {
        const allKeys = new Set([...Object.keys(prevBalances), ...Object.keys(nextBalances)]);
        const now = Date.now();
        for (const fid of allKeys) {
          if (prevBalances[fid] !== nextBalances[fid]) {
            dirtyFundsRef.current.set(fid, now);
          }
        }
      }
    }
    prevFundBalancesRef.current = nextBalances;

    if (!Array.isArray(apiFunds) || apiFunds.length === 0) return orgData;
    const mergedBalances = { ...nextBalances };
    const dirty = dirtyFundsRef.current;
    for (const f of apiFunds) {
      if (!f || typeof f.id !== 'string') continue;
      const balNum = Number(f.balance);
      if (!Number.isFinite(balNum)) continue;
      // Не override если fund «грязный» — UI уже показывает local optimistic.
      if (dirty.has(f.id)) continue;
      mergedBalances[f.id] = balNum;
    }

    // Stage 20a Phase 2 / Чанк 20a.4c.2 (2026-05-13): overlay funds metadata из API.
    // До этого view-layer override только balances. После 20a.4c.2 saveOrg перестаёт
    // отправлять funds в JSON → backend единственный writer для Fund metadata.
    // Без overlay UI продолжал бы читать stale data.funds (JSON frozen).
    //
    // Логика:
    //   - apiFunds[*]  : authoritative metadata из БД (name/percent/color/isArchived)
    //   - state.funds  : authoritative порядок UI + optimistic edits (до refetch)
    //   - Для funds в apiFunds + state.funds: берём metadata из API, position из state
    //   - Для funds только в state (local-only, не сохранён в БД ещё): keep local
    //   - Для funds только в apiFunds (новый от другого юзера): append at end
    //
    // БЕЗ dirty tracking для metadata — это сознательное решение: metadata меняется
    // редко (имя/процент фонда), flicker ~600ms между uOrg и refetch acceptable.
    // Если станет проблемой — добавим dirtyFundsMetaRef как для balances (Чанк 20a.4a).
    const apiFundsMap = new Map();
    for (const f of apiFunds) {
      if (!f || typeof f.id !== 'string') continue;
      apiFundsMap.set(f.id, {
        id: f.id,
        name: typeof f.name === 'string' ? f.name : '',
        percent: Number.isFinite(Number(f.percent)) ? Number(f.percent) : 0,
        color: f.color || null,
        archived: !!f.isArchived,
      });
    }
    const stateFunds = Array.isArray(orgData.funds) ? orgData.funds : [];
    const seenIds = new Set();
    const mergedFunds = [];
    for (const f of stateFunds) {
      if (!f || !f.id) continue;
      seenIds.add(f.id);
      const apiMeta = apiFundsMap.get(f.id);
      if (apiMeta) {
        // API knows this fund — prefer its metadata, keep UI position.
        mergedFunds.push(apiMeta);
      } else {
        // Local-only (only just created, API didn't catch up yet) — keep as is.
        mergedFunds.push(f);
      }
    }
    // Funds в API но не в state.funds — append (created by other client).
    for (const [id, meta] of apiFundsMap) {
      if (!seenIds.has(id)) mergedFunds.push(meta);
    }

    // Stage 20a Phase 2 / Чанк 20a.6a (2026-05-14): symmetric overlay для accountBalances.
    // Тот же паттерн что для fundBalances: dirty tracking + merge prefer API кроме dirty.
    // apiAccounts[*].balance — authoritative из БД (Чанк 2C+20a.3 атомарно).
    // syncData.accountBalances остаётся как fallback пока 20a.6b/c не отрежут.
    const nextAccBalances = orgData.accountBalances || {};
    const prevAccBalances = prevAccountBalancesRef.current;
    // Чанк A.1 (2026-05-15): см. fundBalances выше. После Stage 20a Phase 2
    // accountBalances вырезано из payload. Переход prevAccBalances X→{}
    // не должен помечать dirty — иначе overlay из apiAccounts.balance не сработает
    // и UI покажет 0,00 ₸ на счетах при сохранённых данных в БД.
    if (prevAccBalances !== null) {
      const nextIsEmpty = Object.keys(nextAccBalances).length === 0;
      if (!nextIsEmpty) {
        const allAccKeys = new Set([...Object.keys(prevAccBalances), ...Object.keys(nextAccBalances)]);
        const now = Date.now();
        for (const aid of allAccKeys) {
          if (prevAccBalances[aid] !== nextAccBalances[aid]) {
            dirtyAccountsRef.current.set(aid, now);
          }
        }
      }
    }
    prevAccountBalancesRef.current = nextAccBalances;

    let mergedAccBalances = nextAccBalances;
    if (Array.isArray(apiAccounts) && apiAccounts.length > 0) {
      mergedAccBalances = { ...nextAccBalances };
      const dirtyAcc = dirtyAccountsRef.current;
      for (const a of apiAccounts) {
        if (!a || typeof a.id !== 'string') continue;
        const balNum = Number(a.balance);
        if (!Number.isFinite(balNum)) continue;
        if (dirtyAcc.has(a.id)) continue; // optimistic UI — local wins
        mergedAccBalances[a.id] = balNum;
      }
    }

    // Stage 21.4 (2026-05-14): overlay для auditLog из AuditLog таблицы (REST).
    // apiAuditLog авторитативный (из БД через 21.3 миграцию + 21.2 dual-write).
    // state.auditLog — fallback (если REST не отвечает) ИЛИ optimistic local entries
    // (после frontend addAuditLog но до 21.5 REST POST). Merge by id (preserve local-only).
    let mergedAuditLog = orgData.auditLog || [];
    if (Array.isArray(apiAuditLog)) {
      const apiIds = new Set();
      for (const e of apiAuditLog) {
        if (e && typeof e.id === 'string') apiIds.add(e.id);
      }
      // Local-only entries (frontend addAuditLog не успело синхронизироваться) — keep.
      const localOnly = (orgData.auditLog || []).filter(e => e && typeof e.id === 'string' && !apiIds.has(e.id));
      // API entries уже отсортированы desc по date (см. audit.js GET orderBy createdAt desc).
      mergedAuditLog = [...localOnly, ...apiAuditLog];
    }

    // Stage 20b Phase 2 / Чанк B.2: overlay categories из Category таблицы.
    let mergedIncomeCategories = orgData.incomeCategories || [];
    let mergedExpenseCategories = orgData.expenseCategories || [];
    if (Array.isArray(apiCategories)) {
      const apiInc = apiCategories.filter(c => c && c.type === 'income').map(c => ({
        id: c.id,
        name: typeof c.name === 'string' ? c.name : '',
        accountId: c.accountId || '',
        contractorIds: Array.isArray(c.contractorIds) ? c.contractorIds : [],
        archived: !!c.isArchived,
      }));
      const apiExp = apiCategories.filter(c => c && c.type === 'expense').map(c => ({
        id: c.id,
        name: typeof c.name === 'string' ? c.name : '',
        fundId: c.fundId || '',
        contractorIds: Array.isArray(c.contractorIds) ? c.contractorIds : [],
        archived: !!c.isArchived,
      }));
      const apiIncIds = new Set(apiInc.map(c => c.id));
      const apiExpIds = new Set(apiExp.map(c => c.id));
      const localOnlyInc = (orgData.incomeCategories || []).filter(c => c && c.id && !apiIncIds.has(c.id));
      const localOnlyExp = (orgData.expenseCategories || []).filter(c => c && c.id && !apiExpIds.has(c.id));
      mergedIncomeCategories = [...localOnlyInc, ...apiInc];
      mergedExpenseCategories = [...localOnlyExp, ...apiExp];
    }

    // Чанк B (2026-05-15): централизованный enrichment transactions поверх mergedFunds /
    // mergedCategories. Backend syncDataMappers.operationToJson возвращает fundName / 
    // incomeCategoryName / expenseCategoryName пустыми строками. Раньше каждый компонент
    // делал свой inline lookup в data.funds.find() / data.expenseCategories.find() — кто-то
    // правильно, кто-то нет (AFTab.getRouteLabel, TxTab колонка ФОНД, OpsTab.recent place,
    // TxEditModal SearchableSelect, DayDetailsModal). Now: enrich здесь, all consumers get
    // correct names в tx.fundName / tx.incomeCategoryName / tx.expenseCategoryName.
    //
    // DEFENSIVE: enrich ТОЛЬКО если поле пустое. Если фронт через uOrg сам заполнил name
    // (например QuickIncomeModal знает свежую категорию до её появления в apiCategories) —
    // не override. Это защищает оптимистичный UI от race условий с polling.
    //
    // TYPE-AWARE: lookup только в array соответствующего типа. Если tx.type='expense' и
    // expenseCategoryId указывает на id, отсутствующий в mergedExpenseCategories
    // (legacy data mismatch / удалённая категория) — fallback 'Без категории'. НЕ ищем
    // в mergedIncomeCategories — иначе показали бы доходную категорию для расхода.
    const enrichedTransactions = (orgData.transactions || []).map(tx => {
      let next = tx;
      // Fund name (single)
      if (next.fundId && !next.fundName) {
        const f = mergedFunds.find(f => f && f.id === next.fundId);
        if (f && f.name) next = { ...next, fundName: f.name };
      }
      // To-fund name (для transfer типа 'fund')
      if (next.toFundId && !next.toFundName) {
        const tf = mergedFunds.find(f => f && f.id === next.toFundId);
        if (tf && tf.name) next = { ...next, toFundName: tf.name };
      }
      // Category name — type-aware. Для income lookup только в income, для expense — в expense.
      // Чанк G (2026-05-15): убран fallback 'Без категории' для случая когда lookup не
      // нашёл cat в mergedCategories (transient race с apiCategories refetch / удалённая
      // категория). До этого UI показывал misleading "Без категории" на 1-2 секунды
      // после save: polling refresh ставил tx.*CategoryName='', enrichment lookup'ил,
      // не находил (race), и ставил 'Без категории' вместо реального имени до прихода
      // обновлённого apiCategories. Теперь: если lookup miss — оставляем '' и UI
      // показывает "—" или пропускает category в format. Tx БЕЗ categoryId уже
      // получает 'Без категории' через normalizeOrgData (shared.js) — это правильная
      // семантика для "категория не выбрана", не для "категория временно не загружена".
      if (next.type === 'income' && next.incomeCategoryId && !next.incomeCategoryName) {
        const cat = mergedIncomeCategories.find(c => c && c.id === next.incomeCategoryId);
        if (cat && cat.name) {
          next = { ...next, incomeCategoryName: cat.name };
        }
      }
      if (next.type === 'expense' && next.expenseCategoryId && !next.expenseCategoryName) {
        const cat = mergedExpenseCategories.find(c => c && c.id === next.expenseCategoryId);
        if (cat && cat.name) {
          next = { ...next, expenseCategoryName: cat.name };
        }
      }
      return next;
    });

    return { ...orgData, fundBalances: mergedBalances, funds: mergedFunds, accountBalances: mergedAccBalances, auditLog: mergedAuditLog, incomeCategories: mergedIncomeCategories, expenseCategories: mergedExpenseCategories, transactions: enrichedTransactions };
  }, [orgData, apiFunds, apiAccounts, apiAuditLog, apiCategories]);

  // Phase 10: авто-скрытие conflict toast через 5 сек
  useEffect(() => {
    if (!syncConflictToast) return;
    const id = setTimeout(() => setSyncConflictToast(null), 5000);
    return () => clearTimeout(id);
  }, [syncConflictToast]);

  // При загрузке: проверяем доступность сервера + если есть JWT — подтягиваем данные
  useEffect(() => {
    // Всегда проверяем доступность сервера (виден в Network)
    api.isServerAvailable().then(ok => {
      console.log('[APP] Server available:', ok);
      setServerOnline(ok);
    });

    const token = api.getToken();
    console.log('[APP] Mount: token =', token ? 'exists' : 'none', '| globalUsers =', (meta.globalUsers || []).length);
    if (!token) return;

    // Пробуем загрузить профиль — если токен валиден
    api.getProfile().then(serverUser => {
      if (!serverUser) return;
      apiModeRef.current = (true);
      // Загружаем оргдоступы
      return api.getOrgs().then(orgs => {
        console.log('[APP] Orgs from server:', orgs.map(o => o.id + ' ' + o.name));

        const gu = {
          ...serverUser,
          orgAccess: orgs.map(o => ({ orgId: o.id, role: o.role, permissions: o.permissions || {} })),
        };
        setGlobalUser(gu);

        // Синхронизируем meta.orgs с серверными ID
        // Серверные орги могут иметь другие ID чем локальные
        setMeta(prev => {
          const localOrgs = prev.orgs || [];
          const serverOrgs = orgs.map(o => ({
            id: o.id,
            name: o.name,
            color: o.color || localOrgs.find(lo => lo.name === o.name)?.color || null,
          }));
          // Мержим: оставляем серверные + добавляем локальные которых нет на сервере
          const serverIds = new Set(serverOrgs.map(o => o.id));
          const serverNames = new Set(serverOrgs.map(o => o.name));
          const onlyLocal = localOrgs.filter(lo => !serverIds.has(lo.id) && !serverNames.has(lo.name));
          const mergedOrgs = [...serverOrgs, ...onlyLocal];
          console.log('[APP] Merged orgs:', mergedOrgs.map(o => o.id + ' ' + o.name));
          return { ...prev, orgs: mergedOrgs };
        });

        // Навигация после загрузки оргдоступов — всегда грузим данные с сервера
        if (gu.orgAccess.length === 1) {
          const oId = gu.orgAccess[0].orgId;
          api.loadOrgFromServer(oId).then(serverData => {
            if (serverData && serverData.name) {
              setOrgId(oId); setOrgData(normalizeOrgData(serverData)); setScreenWithHash('app');
            } else {
              // Сервер вернул пустые данные — попробуем кэш
              const cached = loadOrg(oId);
              if (cached) { setOrgId(oId); setOrgData(cached); setScreenWithHash('app'); }
              else setScreenWithHash('orgselect');
            }
          }).catch(() => {
            // Сервер недоступен — fallback на кэш
            const cached = loadOrg(oId);
            if (cached) { setOrgId(oId); setOrgData(cached); setScreenWithHash('app'); }
            else setScreenWithHash('orgselect');
          });
        } else {
          // 0 или >1 организаций — показываем выбор (там есть кнопка «Создать»)
          setScreenWithHash('orgselect');
        }
        // Фоновая синхронизация meta с сервера (но НЕ перезаписываем orgs — они уже смержены выше)
        return api.loadMetaFromServer().then(serverMeta => {
          if (serverMeta && serverMeta.version) {
            setMeta(prev => {
              const { orgs: _ignore, ...rest } = serverMeta;
              return { ...prev, ...rest };
            });
          }
        });
      });
    }).catch((err) => {
      console.error('[APP] Session restore failed:', err.message);
      // Токен невалиден или сервер недоступен — работаем локально
      apiModeRef.current = (false);
      // Если есть localStorage пользователь — показать home screen
      if ((meta.globalUsers || []).length > 0) {
        setScreenWithHash('home');
      }
    });
  // eslint-disable-next-line
  }, []);

  // Получить текущую роль/пермишны пользователя для текущей орг
  const getOrgAccess = (gUser, oId) => {
    if (!gUser || !oId) return null;
    return (gUser.orgAccess || []).find(a => a.orgId === oId);
  };

  const currentAccess = getOrgAccess(globalUser, orgId);
  // Совместимый объект user для передачи в компоненты (как раньше)
  const user = globalUser && currentAccess ? {
    id: globalUser.id, name: globalUser.name, login: globalUser.login,
    role: currentAccess.role, permissions: currentAccess.permissions || {},
  } : null;
  const perms = user ? { ...ROLE_PERMS[user.role], ...(user.permissions || {}) } : {};

  // Организации, доступные текущему пользователю
  const userOrgs = globalUser
    ? (meta.orgs || []).filter(o => (globalUser.orgAccess || []).some(a => a.orgId === o.id))
    : [];

  // Состояние загрузки (для API-запросов)
  const [loading, setLoading] = useState(false);
  const [loginErr, setLoginErr] = useState('');
  // Stage 11 UI: bridge state для 2FA flow. Хранит tempToken между /login и /2fa/verify.
  // Когда != null — рендерится <TwoFAScreen /> поверх остальных экранов.
  const [pending2FA, setPending2FA] = useState(null);
  const apiModeRef = useRef(!!api.getToken()); // true если есть JWT
  const [serverOnline, setServerOnline] = useState(false); // сервер доступен?

  // Хелпер: после логина — открыть нужный экран (всегда грузим данные с сервера)
  const afterLogin = async (gu) => {
    setGlobalUser(gu);
    if (gu.orgAccess.length === 1) {
      const oId = gu.orgAccess[0].orgId;
      let d = null;
      // Всегда грузим с сервера
      if (api.getToken()) {
        try {
          const serverData = await api.loadOrgFromServer(oId);
          if (serverData && serverData.name) {
            d = normalizeOrgData(serverData);
            console.log('[APP] afterLogin: loaded org from server:', d.name);
          }
        } catch (e) { console.warn('[APP] afterLogin: server unavailable:', e.message); }
      }
      // Fallback на кэш если сервер недоступен
      if (!d) d = loadOrg(oId);
      if (d) { setOrgId(oId); setOrgData(d); setScreenWithHash('app'); }
      else setScreenWithHash('orgselect');
    } else if (gu.orgAccess.length === 0) {
      setScreenWithHash('orgselect');
    } else {
      setScreenWithHash('orgselect');
    }
  };

  // Stage 11: завершение логина после успешного /2fa/verify.
  // На входе serverUser из api.verify2FA (токены уже сохранены api клиентом).
  // Повторяет ту же логику что doLogin делает после api.login: загружает meta+orgs,
  // мерджит в setMeta, и вызывает afterLogin для перехода в App.
  const complete2FALogin = (serverUser) => {
    console.log('[APP] 2FA verified, completing login for:', serverUser.id);
    apiModeRef.current = (true);
    setLoading(true);
    return Promise.all([
      api.loadMetaFromServer().catch(() => null),
      api.getOrgs().catch(() => []),
    ]).then(([serverMeta, orgs]) => {
      if (serverMeta && serverMeta.version) {
        setMeta(prev => ({ ...prev, ...serverMeta }));
      }
      if (orgs.length > 0) {
        setMeta(prev => {
          const localOrgs = prev.orgs || [];
          const serverOrgs = orgs.map(o => ({
            id: o.id, name: o.name, color: o.color || localOrgs.find(lo => lo.name === o.name)?.color || null,
          }));
          const serverIds = new Set(serverOrgs.map(o => o.id));
          const merged = [...serverOrgs, ...localOrgs.filter(lo => !serverIds.has(lo.id))];
          return { ...prev, orgs: merged };
        });
      }
      afterLogin({
        id: serverUser.id, login: serverUser.login, name: serverUser.name,
        orgAccess: orgs.map(o => ({ orgId: o.id, role: o.role, permissions: o.permissions || {} })),
      });
    }).finally(() => setLoading(false));
  };

  // Глобальный логин — сначала API, потом fallback на localStorage
  const doLogin = (login, password) => {
    console.log('[APP] doLogin called:', login);
    setLoading(true);
    setLoginErr('');

    api.login(login, password).then(serverUser => {
      // Stage 11: bridge для 2FA. Если у юзера включена 2FA — бэкенд вернул
      // {needs2FA: true, tempToken} вместо access/refresh. Сохраняем tempToken
      // в pending2FA state — рендер переключится на TwoFAScreen.
      // Не вызываем afterLogin здесь: верификация кода + завершение логина
      // произойдут в onSuccess коллбэке TwoFAScreen.
      if (serverUser && serverUser.needs2FA && serverUser.tempToken) {
        console.log('[APP] Login: 2FA required');
        setPending2FA({ tempToken: serverUser.tempToken });
        return null;
      }
      console.log('[APP] Login success, server user:', serverUser.id);
      apiModeRef.current = (true);
      // Загружаем мета и оргдоступы с сервера
      return Promise.all([
        api.loadMetaFromServer().catch(() => null),
        api.getOrgs().catch(() => []),
      ]).then(([serverMeta, orgs]) => {
        if (serverMeta && serverMeta.version) {
          setMeta(prev => ({ ...prev, ...serverMeta }));
        }
        // Синхронизируем meta.orgs с серверными (на новом устройстве meta.orgs может быть пустой)
        if (orgs.length > 0) {
          setMeta(prev => {
            const localOrgs = prev.orgs || [];
            const serverOrgs = orgs.map(o => ({
              id: o.id, name: o.name, color: o.color || localOrgs.find(lo => lo.name === o.name)?.color || null,
            }));
            const serverIds = new Set(serverOrgs.map(o => o.id));
            const merged = [...serverOrgs, ...localOrgs.filter(lo => !serverIds.has(lo.id))];
            return { ...prev, orgs: merged };
          });
        }
        afterLogin({
          id: serverUser.id, login: serverUser.login, name: serverUser.name,
          orgAccess: orgs.map(o => ({ orgId: o.id, role: o.role, permissions: o.permissions || {} })),
        });
      });
    }).catch(err => {
      console.log('[APP] Login API error:', err.message, '| isOffline:', err.isOffline, '| status:', err.status);
      // API вернул ошибку ИЛИ сервер недоступен
      if (err.isOffline) {
        // Сервер недоступен — fallback на localStorage
        const gu = (meta.globalUsers || []).find(u => u.login === login && u.password === password);
        if (!gu) { setLoginErr(S().login.wrongCredentials); return; }
        apiModeRef.current = (false);
        afterLogin(gu);
      } else {
        // Сервер доступен, но не знает этого пользователя (401/404) или неверный пароль
        // Пробуем localStorage
        const gu = (meta.globalUsers || []).find(u => u.login === login && u.password === password);
        if (gu) {
          // Пользователь есть в localStorage, но НЕТ на сервере → авто-регистрация на сервере.
          // Stage 11: email при auto-reg НЕ передаём — у legacy-юзера нет свежего OTP-токена,
          // а локальный пароль не доказывает владение email. Юзер привяжет email потом
          // через /me/email/request (там OTP-flow).
          api.register(login, password, gu.name, '', gu.phone || '', getStoredReferralCode()).then(serverUser => {
            clearStoredReferralCode();
            apiModeRef.current = (true);
            // Синхронизируем все существующие организации на сервер
            const orgAccesses = gu.orgAccess || [];
            const orgPromises = orgAccesses.map(a => {
              const localOrg = loadOrg(a.orgId);
              const orgMeta = (meta.orgs || []).find(o => o.id === a.orgId);
              if (orgMeta) {
                return api.createOrg({
                  name: orgMeta.name,
                  color: orgMeta.color,
                  currency: localOrg?.currency || 'KZT',
                }).catch(() => null);
              }
              return Promise.resolve(null);
            });
            return Promise.all(orgPromises).then(serverOrgs => {
              // Подтягиваем актуальные оргдоступы с сервера
              return api.getOrgs().catch(() => []);
            }).then(orgs => {
              // Обновляем globalUser с серверными orgAccess
              const updatedGu = {
                ...gu,
                id: serverUser.id,
                orgAccess: orgs.length > 0
                  ? orgs.map(o => ({ orgId: o.id, role: o.role, permissions: o.permissions || {} }))
                  : gu.orgAccess,
              };
              setGlobalUser(updatedGu);
              // Синхронизируем мета на сервер
              api.syncMeta(meta).catch(e => console.warn('[SYNC]', e));
            });
          }).catch(() => {
            // Регистрация не удалась — работаем локально
            apiModeRef.current = (false);
          });
          afterLogin(gu);
        } else {
          setLoginErr(err.message || S().login.wrongCredentials);
        }
      }
    }).finally(() => setLoading(false));
    return true;
  };

  // Переключение организации — всегда грузим данные с сервера
  const switchOrg = async (id) => {
    if (id === orgId) return; // уже в этой org
    // Сразу сбрасываем данные — MainApp размонтируется, показывается загрузка
    setOrgId(id);
    setOrgData(null);

    let d = null;
    if (api.getToken()) {
      try {
        const serverData = await api.loadOrgFromServer(id);
        if (serverData && serverData.name) {
          d = normalizeOrgData(serverData);
          console.log('[APP] switchOrg: loaded from server:', d.name);
        }
      } catch (e) {
        if (e.status === 403) {
          console.warn('[APP] switchOrg: access revoked for org', id);
          alert(S().admin.accessRevoked || 'Доступ к организации отозван');
          try {
            const orgs = await api.getOrgs();
            if (globalUser) setGlobalUser({ ...globalUser, orgAccess: orgs });
          } catch {}
          setScreenWithHash('orgselect');
          return;
        }
        console.warn('[APP] switchOrg: server unavailable:', e.message);
      }
    }
    // Fallback на кэш
    if (!d) d = loadOrg(id);
    if (d) { setOrgData(d); setScreenWithHash('app'); }
    else { console.warn('[APP] Org data not found:', id); setScreenWithHash('orgselect'); }
  };

  const openOrg = async (id) => {
    let d = null;
    if (api.getToken()) {
      try {
        const serverData = await api.loadOrgFromServer(id);
        if (serverData && serverData.name) d = normalizeOrgData(serverData);
      } catch (e) { console.warn('[APP] openOrg: server unavailable:', e.message); }
    }
    if (!d) d = loadOrg(id);
    if (d) { setOrgId(id); setOrgData(d); setScreenWithHash('login'); }
  };

  const finishSetup = (name, accs, funds, usr, orgColor) => {
    const id = 'org_' + uid();
    const d = createOrgData(name, accs, funds, usr);
    saveOrg(id, d);
    const color = orgColor || ORG_COLORS[(meta.orgs || []).length % ORG_COLORS.length].hex;

    // Добавляем орг в мета и пользователя в globalUsers (localStorage — мгновенно)
    uMeta(p => {
      const newOrgs = [...(p.orgs || []), { id, name, color }];
      let newGU = [...(p.globalUsers || [])];
      if (globalUser) {
        newGU = newGU.map(gu => gu.id === globalUser.id
          ? { ...gu, orgAccess: [...(gu.orgAccess||[]), { orgId: id, role: 'owner', permissions: {} }] }
          : gu
        );
      } else {
        const existing = newGU.find(gu => gu.login === usr.login);
        if (existing) {
          newGU = newGU.map(gu => gu.id === existing.id
            ? { ...gu, orgAccess: [...(gu.orgAccess||[]), { orgId: id, role: usr.role || 'owner', permissions: {} }] }
            : gu
          );
        } else {
          newGU.push({
            id: usr.id, login: usr.login, password: usr.password, name: usr.name,
            orgAccess: [{ orgId: id, role: usr.role || 'owner', permissions: {} }],
          });
        }
      }
      return { ...p, orgs: newOrgs, globalUsers: newGU };
    });

    // Обновляем globalUser в памяти
    if (globalUser) {
      setGlobalUser(gu => ({
        ...gu,
        orgAccess: [...(gu.orgAccess||[]), { orgId: id, role: 'owner', permissions: {} }],
      }));
    } else {
      setGlobalUser({
        ...usr,
        orgAccess: [{ orgId: id, role: usr.role || 'owner', permissions: {} }],
      });
    }

    // КРИТИЧНО: сначала сбрасываем orgId чтобы useEffect [orgId, orgData]
    // НЕ перезаписал данные предыдущей орг данными новой орг
    setOrgId(null);
    setOrgData(d);

    // === Синхронизация с API (ждём серверный UUID перед показом app) ===
    const doServerSync = async () => {
      console.log('[APP] doServerSync: token =', api.getToken() ? 'exists' : 'none', '| globalUser =', !!globalUser);
      let finalOrgId = id;
      try {
        // Шаг 1: Регистрация (если ещё нет токена и нет текущего globalUser)
        if (!api.getToken() && !globalUser) {
          console.log('[APP] Registering user on server:', usr.login);
          const serverUser = await api.register(usr.login, usr.password, usr.name, usr.email || '', usr.phone || '', getStoredReferralCode(), usr.emailVerificationToken);
          clearStoredReferralCode();
          console.log('[APP] Registration OK, server user id:', serverUser.id);
          apiModeRef.current = (true);
        }

        // Шаг 2: Создаём организацию на сервере (если есть токен)
        if (api.getToken()) {
          console.log('[APP] Creating org on server:', name);
          const serverOrg = await api.createOrg({ name, color, currency: d.currency || 'KZT' });
          console.log('[APP] Org created on server:', serverOrg.id, '| local id:', id);

          // КРИТИЧНО: Обновляем локальный orgId на серверный UUID
          if (serverOrg && serverOrg.id && serverOrg.id !== id) {
            const serverId = serverOrg.id;
            finalOrgId = serverId;
            // Копируем данные орг из локального id в серверный
            saveOrg(serverId, d);
            try { localStorage.removeItem('finup_org_' + id); } catch(e) {}
            // Обновляем meta.orgs и globalUser.orgAccess — заменяем локальный id на серверный
            uMeta(p => ({
              ...p,
              orgs: (p.orgs || []).map(o => o.id === id ? { ...o, id: serverId } : o),
              globalUsers: (p.globalUsers || []).map(gu => ({
                ...gu,
                orgAccess: (gu.orgAccess || []).map(a => a.orgId === id ? { ...a, orgId: serverId } : a),
              })),
            }));
            setGlobalUser(gu => gu ? ({
              ...gu,
              orgAccess: (gu.orgAccess || []).map(a => a.orgId === id ? { ...a, orgId: serverId } : a),
            }) : gu);
            console.log('[APP] Replaced local orgId', id, '→', serverId);
          }

          // Шаг 3: Синхронизируем данные организации (blob)
          if (serverOrg && serverOrg.id) {
            api.syncOrg(serverOrg.id, d).catch(e => console.warn('[APP] syncOrg failed:', e.message));
            // Чанк 20a.4c.1: создаём funds в нормализованной таблице Fund через REST.
            // Делается ПОСЛЕ createOrg чтобы Org row существовал (FK constraint).
            // saveOrg.funds всё ещё пишет JSON (20a.4b cut только fundBalances), в 20a.4c.2 финальный cut.
            if (Array.isArray(d.funds)) {
              const fbMap = d.fundBalances || {};
              for (const f of d.funds) {
                if (!f || typeof f !== 'object' || !f.id || !f.name) continue;
                api.createFund(serverOrg.id, {
                  id: f.id,
                  name: f.name,
                  percent: parseFloat(f.percent) || 0,
                  color: f.color || null,
                  balance: parseFloat(fbMap[f.id]) || 0,
                }).catch(e => console.warn('[FUNDS API] initial createFund failed for', f.id, ':', e?.message));
              }
            }
            // Hotfix 20a.6a.2 (2026-05-14): создаём accounts в нормализованной таблице Account через REST.
            // Симметрично api.createFund выше. До этого hotfix initial accounts создавались
            // через saveOrg → JSON, но после NORMALIZED_GET_ORGS=ALL backend smart-merge возвращает
            // accounts из БД (пустой таблицы для новой org) → UI показывает 0 счетов.
            if (Array.isArray(d.accounts)) {
              const abMap = d.accountBalances || {};
              for (const a of d.accounts) {
                if (!a || typeof a !== 'object' || !a.id || !a.name) continue;
                api.createAccount(serverOrg.id, {
                  id: a.id,
                  name: a.name,
                  type: a.type || 'cash',
                  balance: parseFloat(abMap[a.id]) || parseFloat(a.balance) || 0,
                  color: a.color || null,
                  isHidden: !!a.isHidden,
                  isPrimary: !!a.isPrimary,
                }).catch(e => console.warn('[ACCOUNTS API] initial createAccount failed for', a.id, ':', e?.message));
              }
            }
          }
          // Шаг 4: Синхронизируем мета
          const currentMeta = loadMeta();
          if (currentMeta) {
            api.syncMeta(currentMeta).catch(e => console.warn('[APP] syncMeta failed:', e.message));
          }
        } else {
          console.warn('[APP] No token after register — server might be down');
        }
      } catch (err) {
        // Сервер недоступен — работаем локально, данные уже в localStorage
        console.error('[APP] Server sync FAILED:', err.message, '| isOffline:', err.isOffline);
      }
      return finalOrgId;
    };

    // Ждём серверный UUID, затем показываем app с правильным orgId
    setLoading(true);
    doServerSync().then(finalId => {
      setOrgId(finalId);
      setOrgData(d); // обновляем на случай если orgData сбросился
      setScreenWithHash('app');
    }).finally(() => setLoading(false));
  };

  // ─── Удаление организации / аккаунта ───
  const [deleteModal, setDeleteModal] = useState(null); // { type: 'org'|'account', orgId?, orgName? }

  const doDeleteOrg = async (id, password) => {
    // Удаляем на сервере (Stage 11: бэкенд требует password для re-auth)
    try {
      await api.deleteOrg(id, password);
    } catch (e) {
      console.error('[APP] Delete org API error:', e);
      // При неверном пароле бэкенд вернёт 401 PASSWORD_INVALID — пробрасываем,
      // чтобы модалка показала ошибку и не закрывалась.
      throw e;
    }
    // Удаляем локально
    uMeta(p => ({
      ...p,
      orgs: p.orgs.filter(o => o.id !== id),
      globalUsers: (p.globalUsers||[]).map(gu => ({
        ...gu,
        orgAccess: (gu.orgAccess||[]).filter(a => a.orgId !== id),
      })),
    }));
    try { localStorage.removeItem('finup_org_' + id); } catch (e) { }
    if (orgId === id) { setOrgId(null); setOrgData(null); setScreenWithHash('orgselect'); }
    setDeleteModal(null);
  };

  const doDeleteAccount = async (password) => {
    try {
      await api.deleteMyAccount(password);
    } catch (e) {
      console.error('[APP] Delete account API error:', e);
      throw e; // пусть модалка покажет ошибку
    }
    api.logout();
    apiModeRef.current = (false);
    setGlobalUser(null);
    setOrgId(null);
    setOrgData(null);
    setDeleteModal(null);
    setScreenWithHash('home');
  };

  const downloadOrgBackup = (backupOrgId) => {
    const d = loadOrg(backupOrgId);
    const orgMeta = (meta.orgs || []).find(o => o.id === backupOrgId);
    const name = orgMeta?.name || d?.name || 'org';
    const blob = new Blob([JSON.stringify(d, null, 2)], { type: 'application/json' });
    const u = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = u;
    a.download = `inmonex-backup-${name}-${new Date().toISOString().slice(0,10)}.json`;
    a.click();
    URL.revokeObjectURL(u);
  };

  const downloadFullBackup = () => {
    const allData = {
      user: globalUser,
      meta: meta,
      organizations: (userOrgs || []).map(o => ({
        ...o,
        data: loadOrg(o.id),
      })),
      exportDate: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(allData, null, 2)], { type: 'application/json' });
    const u = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = u;
    a.download = `inmonex-full-backup-${globalUser?.name || 'user'}-${new Date().toISOString().slice(0,10)}.json`;
    a.click();
    URL.revokeObjectURL(u);
  };

  // Тема: если есть активная орг с цветом — генерируем из неё
  const currentOrgInfo = orgId ? (meta.orgs || []).find(o => o.id === orgId) : null;
  const orgThemes = currentOrgInfo?.color ? generateOrgTheme(currentOrgInfo.color) : null;
  const baseTheme = meta.theme || 'light';
  const theme = orgThemes ? orgThemes[baseTheme] : THEMES[baseTheme];

  return (
    <ThemeCtx.Provider value={theme}>
      <style>{getCSS(theme)}</style>
      {screen === 'home' && !globalUser && <HomeScreen meta={meta} onOpen={openOrg} onNew={() => setScreenWithHash('setup')} toggle={toggle} onLogin={doLogin} changeLang={changeLang} loading={loading} loginErr={loginErr} clearLoginErr={() => setLoginErr('')} serverOnline={serverOnline} />}
      {screen === 'orgselect' && globalUser && <OrgSelectScreen orgs={userOrgs} meta={meta} globalUser={globalUser} onSelect={switchOrg} onNew={() => setScreenWithHash('setup')} toggle={toggle} changeLang={changeLang} onLogout={() => { api.logout(); apiModeRef.current = (false); setGlobalUser(null); setOrgId(null); setOrgData(null); setScreenWithHash('home') }} onDeleteOrg={(id, name) => setDeleteModal({ type: 'org', orgId: id, orgName: name })} onDeleteAccount={() => setDeleteModal({ type: 'account' })} />}
      {deleteModal && <DeleteModal mode={deleteModal.type} orgId={deleteModal.orgId} orgName={deleteModal.orgName} onDeleteOrg={doDeleteOrg} onDeleteAccount={doDeleteAccount} onDownloadOrg={downloadOrgBackup} onDownloadFull={downloadFullBackup} onClose={() => setDeleteModal(null)} />}
      {/* Phase 10: toast при VERSION_CONFLICT — другой юзер успел сохранить, мы применили его данные */}
      {syncConflictToast && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9998, background: theme.bgCard, color: theme.text, border: `2px solid ${theme.accentRed || '#ef4444'}`, borderRadius: 12, padding: '14px 18px', boxShadow: '0 8px 24px rgba(0,0,0,.25)', maxWidth: 360, fontSize: 13, lineHeight: 1.4 }}>
          <div style={{ fontWeight: 700, marginBottom: 4, color: theme.accentRed || '#ef4444' }}>{S().sync?.conflictTitle || 'Данные обновлены'}</div>
          <div style={{ color: theme.textSec }}>{S().sync?.conflictBody || 'Другой пользователь сохранил изменения раньше. Применили свежую версию — повторите ваше действие, если нужно.'}</div>
        </div>
      )}
      {screen === 'setup' && <>
        <SetupWizard onFinish={finishSetup} onCancel={() => setScreenWithHash(globalUser ? 'orgselect' : 'home')} meta={meta} toggle={toggle} changeLang={changeLang} userOrgs={userOrgs} existingUser={globalUser} />
        {loading && <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div style={{ background: theme.bgCard, borderRadius: 16, padding: '32px 40px', textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,.3)' }}><div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div><div style={{ fontSize: 14, fontWeight: 700 }}>{S().common.saving || 'Сохранение...'}</div></div></div>}
      </>}
      {screen === 'login' && orgData && <LoginScreen org={orgData} onLogin={(u) => { doLogin(u.login, u.password) || (() => { setGlobalUser({...u, orgAccess:[{orgId,role:u.role,permissions:u.permissions||{}}]}); setScreenWithHash('app') })() }} onBack={() => { setOrgId(null); setOrgData(null); setScreenWithHash('home') }} toggle={toggle} tn={baseTheme} changeLang={changeLang} meta={meta} />}
      {screen === 'app' && orgData && user && <MainApp key={orgId} data={orgDataView} uOrg={uOrg} user={user} perms={perms} meta={meta} uMeta={uMeta} globalUser={globalUser} setGlobalUser={setGlobalUser} userOrgs={userOrgs} orgId={orgId} switchOrg={switchOrg} onLogout={() => { api.logout(); apiModeRef.current = (false); setGlobalUser(null); setOrgId(null); setOrgData(null); setScreenWithHash('home') }} onHome={() => { setScreenWithHash('orgselect') }} onAddOrg={() => setScreenWithHash('setup')} toggle={toggle} tn={baseTheme} changeLang={changeLang} onDeleteOrg={() => { const orgMeta = (meta.orgs || []).find(o => o.id === orgId); setDeleteModal({ type: 'org', orgId, orgName: orgMeta?.name || orgData?.name || '' }); }} triggerSyncCheck={triggerSyncCheck} lastSyncedAt={lastSyncedAt} />}
      {/* Stage 11: 2FA second-step overlay. Рендерится поверх остальных экранов
          (z-index 9999), когда после /login бэкенд вернул needs2FA + tempToken. */}
      {pending2FA && (
        <TwoFAScreen
          tempToken={pending2FA.tempToken}
          onSuccess={(user) => { setPending2FA(null); complete2FALogin(user); }}
          onBack={() => { setPending2FA(null); setLoginErr(''); }}
          toggle={toggle}
          changeLang={changeLang}
          meta={meta}
        />
      )}
    </ThemeCtx.Provider>
  );
}

// ═══ Универсальная панель выбора языка (табы) + темы ═══
const LANGS = [
  { id: 'ru', flag: '🇷🇺', label: 'Русский', short: 'RU' },
  { id: 'kz', flag: '🇰🇿', label: 'Қазақша', short: 'KZ' },
  { id: 'en', flag: '🇬🇧', label: 'English', short: 'EN' },
];

function LangThemeBar({ changeLang, toggle, meta }) {
  const t = useTheme();
  const current = getLang();
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 16 }}>
      <div style={{
        display: 'flex', borderRadius: 10, overflow: 'hidden',
        border: `1.5px solid ${t.accent}30`, background: t.bgCard,
      }}>
        {LANGS.map(l => (
          <button key={l.id} onClick={() => changeLang(l.id)} style={{
            padding: '6px 14px', fontSize: 12, fontWeight: current === l.id ? 700 : 500,
            background: current === l.id ? `${t.accent}18` : 'transparent',
            color: current === l.id ? t.accent : t.textMuted,
            borderRight: l.id !== 'en' ? `1px solid ${t.accent}20` : 'none',
            cursor: 'pointer', transition: '0.2s', display: 'flex', alignItems: 'center', gap: 5,
          }}>
            <span style={{ fontSize: 13 }}>{l.flag}</span> {l.short}
          </button>
        ))}
      </div>
      <button onClick={toggle} style={{
        padding: '6px 14px', borderRadius: 10, fontSize: 12, fontWeight: 600,
        background: t.bgCard, border: `1.5px solid ${t.accent}30`,
        color: t.accent, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
        transition: '0.2s',
      }}>
        {meta.theme === 'dark' ? <Sun size={13} /> : <Moon size={13} />}
        {meta.theme === 'dark' ? S().home.light : S().home.dark}
      </button>
    </div>
  );
}

function HomeScreen({ meta, onOpen, onNew, toggle, onLogin, changeLang, loading, loginErr, clearLoginErr, serverOnline }) {
  const t = useTheme();
  const [login, setLogin] = useState('');
  const [pass, setPass] = useState('');
  const [show, setShow] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  // Показываем логин-форму если есть пользователи ИЛИ если сервер доступен (пользователь может быть в БД)
  const hasUsers = (meta.globalUsers || []).length > 0 || api.getToken() || serverOnline;

  const doGo = () => {
    if (!hasUsers) { onNew(); return; }
    clearLoginErr();
    onLogin(login, pass);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: t.gradient }}>
      <div style={{ width: 440, maxWidth: '90vw' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: 3, color: t.accent }}>{S().home.brand}</div>
          <div style={{ fontSize: 13, color: t.textMuted, marginTop: 6 }}>{S().home.subtitle}</div>
        </div>

        {hasUsers ? (
          /* Чанк F (2026-05-15): обёрнут в <form> для убирания Chrome DOM warning
             "Password field is not contained in a form" + улучшения работы password
             managers (1Password / Bitwarden / Chrome saved passwords). onSubmit
             вызывает doGo (Enter в form тоже триггерит submit; existing onKeyDown
             handlers остаются как defence-in-depth для browser quirks). */
          <form onSubmit={e => { e.preventDefault(); doGo(); }} style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 12, padding: '24px 20px', marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 16, textAlign: 'center' }}>{S().home.loginTitle}</div>
            <input value={login} onChange={e => setLogin(e.target.value)} placeholder={S().login.loginPlaceholder} onKeyDown={e => e.key === 'Enter' && doGo()} autoComplete="username" style={{ marginBottom: 12 }} />
            <div style={{ position: 'relative', marginBottom: 14 }}>
              {/* Логин: НЕ фильтруем кириллицу — нужно дать legacy-юзерам с кириллическими
                  паролями возможность авторизоваться (бэк делает только bcrypt.compare). */}
              <input type={show ? 'text' : 'password'} value={pass} onChange={e => setPass(e.target.value)} placeholder={S().login.passwordPlaceholder} onKeyDown={e => e.key === 'Enter' && doGo()} autoComplete="current-password" />
              <button type="button" onClick={() => setShow(!show)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'transparent', color: t.textMuted }}>{show ? <EyeOff size={18} /> : <Eye size={18} />}</button>
            </div>
            {loginErr && <div style={{ color: t.accentRed, fontSize: 13, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 4 }}><AlertCircle size={14} /> {loginErr}</div>}
            <button type="submit" disabled={loading} style={{ width: '100%', padding: 12, borderRadius: 10, background: t.accent, color: t.btnText, fontWeight: 700, fontSize: 15, opacity: loading ? 0.6 : 1 }}>{loading ? '...' : S().login.enter}</button>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
              <button type="button" onClick={() => setForgotOpen(true)} style={{ background: 'transparent', color: t.textMuted, fontSize: 12, textDecoration: 'underline', cursor: 'pointer' }}>{S().home.forgotPassword}</button>
              <button type="button" onClick={onNew} style={{ background: 'transparent', color: t.accent, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{S().home.registerLink || 'Регистрация'}</button>
            </div>
          </form>
        ) : (
          <div style={{ marginBottom: 20, textAlign: 'center', color: t.textMuted, fontSize: 13 }}>
            {S().home.createFirstOrg}
          </div>
        )}

        {!hasUsers && (
          <button onClick={onNew} style={{ width: '100%', padding: 14, borderRadius: 10, background: t.accent, color: t.btnText, fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <Plus size={16} />{S().home.createOrg}
          </button>
        )}

        <LangThemeBar changeLang={changeLang} toggle={toggle} meta={meta} />
        <div style={{ textAlign: 'center', marginTop: 24, fontSize: 11, color: t.textMuted }}>{S().home.brand} v{VERSION} © {new Date().getFullYear()}</div>
      </div>
      {forgotOpen && <ForgotPasswordModal onClose={() => setForgotOpen(false)} />}
    </div>
  );
}

// ═══ Модалка "Забыли пароль" ═══
function ForgotPasswordModal({ onClose }) {
  const t = useTheme();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');

  const canSend = email.includes('@') && email.length >= 5 && !loading;

  const submit = async () => {
    if (!canSend) return;
    setErr('');
    setLoading(true);
    try {
      await api.forgotPassword(email.trim().toLowerCase(), getLang());
      setSent(true);
    } catch (e) {
      setErr(e?.message || S().forgotPwd?.failed || 'Ошибка отправки');
    } finally {
      setLoading(false);
    }
  };

  const str = S().forgotPwd || {};

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 420, background: t.bgCard, borderRadius: 14, border: `1px solid ${t.border}`, padding: '28px 24px', boxShadow: '0 8px 32px rgba(0,0,0,.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: t.text }}>{str.title || 'Забыли пароль?'}</div>
          <button onClick={onClose} style={{ background: '#fff5f5', border: '1.5px solid #feb2b2', color: '#c53030', borderRadius: 8, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><X size={16} /></button>
        </div>

        {sent ? (
          <>
            <div style={{ fontSize: 13, color: t.textMuted, marginTop: 12, lineHeight: 1.5 }}>
              {str.sentMsg || 'Если этот email зарегистрирован, мы отправили на него ссылку для сброса пароля. Проверьте почту (в том числе папку Спам). Ссылка действительна 1 час.'}
            </div>
            <button onClick={onClose} style={{ width: '100%', marginTop: 20, padding: 12, borderRadius: 10, background: t.accent, color: t.btnText, fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer' }}>{str.close || 'Закрыть'}</button>
          </>
        ) : (
          <>
            <div style={{ fontSize: 13, color: t.textMuted, marginTop: 8, marginBottom: 12, lineHeight: 1.5 }}>
              {str.desc || 'Введите email, указанный при регистрации. Мы отправим на него ссылку для сброса пароля.'}
            </div>
            {/* Этап 2 v2: подсказка о fallback — если у юзера нет email или нет к нему доступа,
                восстановление возможно через владельца компании ("Персонал → Сменить пароль"). */}
            <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 14, padding: '10px 12px', background: t.bgInput, borderRadius: 8, border: `1px solid ${t.border}`, lineHeight: 1.4, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{str.noEmailHint || 'Если у вас нет email или нет к нему доступа — обратитесь к владельцу компании. Он может сбросить ваш пароль вручную.'}</span>
            </div>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && submit()}
              placeholder={str.emailPh || 'email@example.com'}
              style={{ marginBottom: 12 }}
              autoFocus
            />
            {err && <div style={{ color: t.accentRed, fontSize: 12, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 4 }}><AlertCircle size={13} /> {err}</div>}
            <button onClick={submit} disabled={!canSend} style={{ width: '100%', padding: 12, borderRadius: 10, background: canSend ? t.accent : t.border, color: t.btnText, fontWeight: 700, fontSize: 14, border: 'none', cursor: canSend ? 'pointer' : 'not-allowed', opacity: canSend ? 1 : 0.7 }}>
              {loading ? '...' : (str.sendBtn || 'Отправить ссылку')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ═══ Экран выбора организации (после логина) ═══
function OrgSelectScreen({ orgs, meta, globalUser, onSelect, onNew, toggle, changeLang, onLogout, onDeleteOrg, onDeleteAccount }) {
  const t = useTheme();
  const [loadingId, setLoadingId] = useState(null);
  const handleSelect = async (id) => {
    setLoadingId(id);
    await onSelect(id);
    setLoadingId(null);
  };
  // Проверяем, является ли пользователь владельцем хотя бы одной организации
  const hasOwnerRole = (globalUser.orgAccess || []).some(a => a.role === 'owner');
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <div style={{ width: 480, maxWidth: '92vw' }}>
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: 3, color: t.accent }}>{S().home.brand}</div>
          <div style={{ fontSize: 14, fontWeight: 600, marginTop: 8 }}>{S().orgSelect.welcome(globalUser.name)}</div>
        </div>

        {orgs.length > 0 ? (
          <>
            <div style={{ fontSize: 13, color: t.textSec, marginBottom: 12, textAlign: 'center' }}>{S().orgSelect.choose}</div>

            <div style={{ display: 'grid', gap: 10, marginBottom: 20 }}>
              {orgs.map(o => {
                const access = (globalUser.orgAccess || []).find(a => a.orgId === o.id);
                const isOrgOwner = access?.role === 'owner';
                const orgColor = o.color || t.accent;
                return (
                  <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button onClick={() => handleSelect(o.id)} disabled={!!loadingId} style={{
                      flex: 1, display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px', borderRadius: 12,
                      background: t.bgCard, border: `2px solid ${orgColor}40`, color: t.text, fontSize: 14,
                      fontWeight: 500, textAlign: 'left', transition: '0.2s',
                      opacity: loadingId && loadingId !== o.id ? 0.5 : 1,
                    }}>
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: orgColor, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 16, flexShrink: 0 }}>
                        {loadingId === o.id ? '⏳' : o.name.charAt(0).toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700 }}>{o.name}</div>
                        <div style={{ fontSize: 12, color: t.textMuted }}>{loadingId === o.id ? S().common.loading || 'Загрузка...' : (ROLE_LABELS[access?.role] || access?.role)}</div>
                      </div>
                      <ChevronRight size={16} color={t.textMuted} />
                    </button>
                    {isOrgOwner && (
                      <button onClick={(e) => { e.stopPropagation(); onDeleteOrg(o.id, o.name); }} title={S().deletion.deleteOrg} style={{
                        width: 40, height: 40, borderRadius: 10, background: `${t.accentRed}10`, color: t.accentRed,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0,
                        border: `1px solid ${t.accentRed}20`, transition: '0.15s',
                      }}>
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '32px 20px', background: t.bgCard, borderRadius: 12, border: `1px solid ${t.border}`, marginBottom: 20 }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📋</div>
            <div style={{ fontSize: 14, color: t.textSec, lineHeight: 1.6 }}>
              {S().orgSelect.noOrgs || 'У вас пока нет доступа к организациям. Обратитесь к владельцу организации для получения доступа.'}
            </div>
          </div>
        )}

        {/* Кнопка создания организации — только для владельцев */}
        {hasOwnerRole && (
          <button onClick={onNew} style={{ width: '100%', padding: 14, borderRadius: 10, background: t.accent, color: t.btnText, fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 12 }}>
            <Plus size={16} /> {S().orgSelect.create}
          </button>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 8 }}>
          <button onClick={onLogout} style={{ background: 'transparent', color: t.accentRed, fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}><LogOut size={12} /> {S().sidebar.logout}</button>
          {hasOwnerRole && <button onClick={onDeleteAccount} style={{ background: 'transparent', color: t.textMuted, fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}><Trash2 size={12} /> {S().deletion.deleteAccount}</button>}
        </div>
        <LangThemeBar changeLang={changeLang} toggle={toggle} meta={meta} />

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 10, color: t.textMuted }}>{S().home.brand} v{VERSION}</div>
      </div>
    </div>
  );
}

function SetupWizard({ onFinish, onCancel, meta, toggle, changeLang, userOrgs, existingUser }) {
  const t = useTheme();
  const isExisting = !!existingUser; // Владелец уже авторизован — не спрашивать логин/пароль/email
  const [step, setStep] = useState(1);

  const [orgN, setOrgN] = useState('');
  const [aL, setAL] = useState(isExisting ? (existingUser.login || '') : '');
  const [aP, setAP] = useState(isExisting ? '***' : '');
  const [aP2, setAP2] = useState(isExisting ? '***' : '');
  const [aN, setAN] = useState(isExisting ? (existingUser.name || '') : '');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const [emailVerified, setEmailVerified] = useState(isExisting); // Для существующего пользователя email уже подтверждён
  // Stage 11: токен от /api/email/verify-code; передаётся в /register как доказательство
  // владения email. Без него бэк отклонит регистрацию с email'ом (400).
  const [emailVerificationToken, setEmailVerificationToken] = useState(null);
  const [vStep, setVStep] = useState(0);
  const [code, setCode] = useState('');

  const [accs, setAccs] = useState([{ id: uid(), name: '', iban: '', balance: '', color: '#5b9cf6', archived: false }]);
  const [funds, setFunds] = useState([
    { id: uid(), name: S().defaultFunds.salary, percent: '50', color: '#5b9cf6', archived: false },
    { id: uid(), name: S().defaultFunds.taxes, percent: '20', color: '#f06caa', archived: false },
    { id: uid(), name: S().defaultFunds.profit, percent: '30', color: '#34c77b', archived: false },
  ]);

  const [agreed, setAgreed] = useState(false);
  const [docToShow, setDocToShow] = useState(null);
  const [touched, setTouched] = useState(false);
  const usedColors = (userOrgs || []).map(o => o.color).filter(Boolean);
  const firstAvailable = ORG_COLORS.find(c => !usedColors.includes(c.hex));
  const [orgColor, setOrgColor] = useState(firstAvailable ? firstAvailable.hex : ORG_COLORS[0].hex);

  const tPct = funds.reduce((s, f) => s + (parseFloat(f.percent) || 0), 0);

  const ok1 = isExisting ? orgN.trim() : (orgN.trim() && aL.trim() && aP.trim() && aP.length >= 8 && aP === aP2 && aN.trim() && email.trim() && emailVerified && isValidPhone(phone));
  const accNames = accs.map(a => a.name.trim().toLowerCase()).filter(Boolean);
  const accIbans = accs.map(a => a.iban.trim().toLowerCase()).filter(Boolean);
  const hasDupAccName = accNames.length !== new Set(accNames).size;
  const hasDupIban = accIbans.length !== new Set(accIbans).size;
  const ok2 = accs.length > 0 && accs.every(a => a.name.trim() && a.iban.trim()) && !hasDupAccName && !hasDupIban;
  // Общий начальный баланс по всем счетам (для распределения по фондам)
  const totalInitialBalance = accs.reduce((sum, a) => sum + (parseFloat(a.balance) || 0), 0);
  const fundNames = funds.map(f => f.name.trim().toLowerCase()).filter(Boolean);
  const hasDupFundName = fundNames.length !== new Set(fundNames).size;
  const ok3 = funds.length === 0 || (funds.length > 0 && funds.every(f => f.name.trim() && parseFloat(f.percent) > 0) && tPct === 100 && !hasDupFundName);

  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [codeSending, setCodeSending] = useState(false);

  const sendCode = async () => {
    if (!email.trim()) return;
    setCodeSending(true);
    try {
      await api.sendVerificationCode(email.trim());
      setVStep(1);
      alert(S().setup.codeSent || 'Код отправлен на вашу почту');
    } catch (e) {
      alert(S().setup.codeSendError || 'Не удалось отправить код: ' + e.message);
    } finally {
      setCodeSending(false);
    }
  };

  const doVerify = async () => {
    try {
      const res = await api.verifyCode(email.trim(), code.trim());
      if (res.verified) {
        setEmailVerified(true);
        // Stage 11: сохраняем токен; будет передан в api.register через onFinish.
        setEmailVerificationToken(res.emailVerificationToken || null);
        setVStep(0);
      } else {
        alert(S().setup.wrongCode);
      }
    } catch (e) {
      alert(S().setup.verificationError || 'Ошибка проверки кода');
    }
  };

  const finish = () => {
    const u = isExisting
      ? { id: existingUser.id, login: existingUser.login, password: '', name: existingUser.name, role: 'owner' }
      : { id: uid(), login: aL.trim(), password: aP.trim(), name: aN.trim(), email: email.trim(), phone: phoneDigits(phone), role: 'owner', emailVerificationToken };
    const cleanAccs = accs.map(a => ({ ...a, name: a.name.trim(), iban: a.iban.trim(), balance: parseFloat(a.balance) || 0 }));
    const cleanFunds = funds.map(f => ({ ...f, name: f.name.trim(), percent: parseFloat(f.percent) }));
    onFinish(orgN.trim(), cleanAccs, cleanFunds, u, orgColor);
  };

  const errBorder = (isEmpty) => touched && isEmpty ? { border: `1.5px solid ${t.accentRed}` } : {};
  // Подсветка процентов: красный если сумма != 100% и touched
  const pctInvalid = touched && funds.length > 0 && tPct !== 100;
  const pctBorder = pctInvalid ? { border: `1.5px solid ${t.accentRed}` } : {};

  const [wizWidth, setWizWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 800);
  useEffect(() => { const h = () => setWizWidth(window.innerWidth); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h) }, []);

  const stepTitles = [S().setup.step1Title, S().setup.step2Title, S().setup.step3Title, S().setup.step4Title];
  const stepIcons = ['👤', '💳', '📊', '✅'];

  const isNarrow = wizWidth < 500;

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: t.gradient, padding: isNarrow ? '12px 4px' : '20px 10px' }}>
      <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: isNarrow ? 12 : 16, padding: isNarrow ? '20px 14px' : '32px 28px', width: 540, maxWidth: '100%', boxShadow: t.shadow }}>
        {docToShow && <LegalDocsModal doc={docToShow} onClose={() => setDocToShow(null)} />}

        {/* HEADER WITH PROGRESS */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: t.accent, letterSpacing: 2 }}>{S().setup.title}</div>
            <div style={{ fontSize: 12, color: t.textMuted, marginTop: 2 }}>{S().setup.stepOf(step, 4)}</div>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {[1, 2, 3, 4].map(s => <div key={s} style={{ width: 32, height: 4, borderRadius: 2, background: s <= step ? t.accent : t.border, transition: 'background 0.3s' }} />)}
          </div>
        </div>

        {/* STEP TITLE BLOCK */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, padding: '14px 16px', background: `${t.accent}10`, borderRadius: 12, border: `1px solid ${t.accent}25` }}>
          <div style={{ fontSize: 28, lineHeight: 1 }}>{stepIcons[step - 1]}</div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: t.text }}>{stepTitles[step - 1]}</div>
            <div style={{ fontSize: 12, color: t.textSec, marginTop: 2, lineHeight: 1.4 }}>
              {step === 1 && S().setup.step1Desc}
              {step === 2 && S().setup.step2Desc}
              {step === 3 && S().setup.step3Desc}
              {step === 4 && S().setup.step4Desc}
            </div>
          </div>
        </div>

        {/* ══════════ STEP 1: REGISTRATION ══════════ */}
        {step === 1 && (
          <div style={{ display: 'grid', gap: 14 }}>
            {/* Если владелец уже авторизован — показываем инфо */}
            {isExisting && (
              <div style={{ padding: '12px 16px', background: `${t.accentGreen}10`, borderRadius: 10, border: `1px solid ${t.accentGreen}25`, display: 'flex', alignItems: 'center', gap: 10 }}>
                <Check size={18} color={t.accentGreen} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: t.accentGreen }}>{S().setup.existingUserInfo || 'Вы уже авторизованы'}</div>
                  <div style={{ fontSize: 12, color: t.textSec }}>{existingUser.name} ({existingUser.login})</div>
                </div>
              </div>
            )}

            <div>
              <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 6, display: 'block' }}>{S().setup.companyName} *</label>
              <input value={orgN} onChange={e => setOrgN(e.target.value)} placeholder={S().setup.companyPlaceholder} style={{ width: '100%', ...errBorder(!orgN.trim()) }} autoFocus />
            </div>

            <div>
              <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 6, display: 'block' }}>{S().setup.orgColorLabel}</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {ORG_COLORS.map(c => {
                  const taken = usedColors.includes(c.hex);
                  return (
                    <button key={c.id} onClick={() => { if (!taken) setOrgColor(c.hex) }} title={taken ? S().setup.orgColorTaken(c.label) : c.label} style={{
                      width: 32, height: 32, borderRadius: 8, background: c.hex, cursor: taken ? 'not-allowed' : 'pointer',
                      border: orgColor === c.hex ? '3px solid #fff' : '2px solid transparent',
                      outline: orgColor === c.hex ? `2px solid ${c.hex}` : 'none',
                      opacity: taken ? 0.25 : 1,
                      transition: '0.15s', position: 'relative',
                    }}>
                      {taken && <X size={16} style={{ color: '#fff', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }} />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Логин/пароль/email — только для новых пользователей */}
            {!isExisting && <>
              <div style={{ display: 'grid', gridTemplateColumns: isNarrow ? '1fr' : '1fr 1fr', gap: isNarrow ? 10 : 14 }}>
                <div>
                  <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 6, display: 'block' }}>{S().setup.yourName} *</label>
                  <input value={aN} onChange={e => setAN(e.target.value)} placeholder={S().setup.namePlaceholder} style={{ width: '100%', ...errBorder(!aN.trim()) }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 6, display: 'block' }}>{S().setup.loginLabel} *</label>
                  <input value={aL} onChange={e => setAL(e.target.value)} placeholder={S().setup.loginPlaceholder} style={{ width: '100%', ...errBorder(!aL.trim()) }} />
                </div>
              </div>

              <div>
                <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 6, display: 'block' }}>{S().setup.passwordLabel} *</label>
                <div style={{ position: 'relative' }}>
                  <input type={showPw ? 'text' : 'password'} value={aP} onChange={e => setAP(stripCyrillic(e.target.value))} placeholder="••••••••" style={{ width: '100%', paddingRight: 40, ...errBorder(!aP.trim() || (touched && aP.length < 8)) }} />
                  <button onClick={() => setShowPw(!showPw)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'transparent', color: t.textMuted, padding: 0, border: 'none', cursor: 'pointer' }}>{showPw ? <EyeOff size={18} /> : <Eye size={18} />}</button>
                </div>
                {touched && aP.trim() && aP.length < 8 && <div style={{ fontSize: 11, color: t.accentRed, marginTop: 4 }}>{S().setup.passwordMinLength}</div>}
              </div>

              <div>
                <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 6, display: 'block' }}>{S().setup.confirmPasswordLabel} *</label>
                <div style={{ position: 'relative' }}>
                  <input type={showPw2 ? 'text' : 'password'} value={aP2} onChange={e => setAP2(stripCyrillic(e.target.value))} placeholder={S().setup.confirmPasswordPlaceholder} style={{ width: '100%', paddingRight: 40, ...errBorder(!aP2.trim() || (touched && aP2 && aP !== aP2)) }} />
                  <button onClick={() => setShowPw2(!showPw2)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'transparent', color: t.textMuted, padding: 0, border: 'none', cursor: 'pointer' }}>{showPw2 ? <EyeOff size={18} /> : <Eye size={18} />}</button>
                </div>
                {touched && aP2.trim() && aP !== aP2 && <div style={{ fontSize: 11, color: t.accentRed, marginTop: 4 }}>{S().setup.passwordsDoNotMatch}</div>}
              </div>

              <div>
                <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 6, display: 'block' }}>{S().setup.emailLabel} *</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input disabled={emailVerified} value={email} onChange={e => { setEmail(e.target.value); setEmailVerified(false); setEmailVerificationToken(null) }} placeholder={S().setup.emailPlaceholder} style={{ flex: 1, opacity: emailVerified ? 0.7 : 1, ...errBorder(!email.trim() || !emailVerified) }} />
                  {emailVerified ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0 14px', background: `${t.accentGreen}15`, color: t.accentGreen, borderRadius: 8, fontWeight: 700, fontSize: 13 }}>
                      <Check size={16} /> {S().setup.emailConfirmed}
                    </div>
                  ) : (
                    <button onClick={sendCode} disabled={!email.trim() || vStep === 1 || codeSending} style={{ padding: '0 14px', color: email.trim() ? t.accent : t.textMuted, background: t.bgInput, border: `1px solid ${email.trim() ? t.accent : t.border}`, borderRadius: 8, fontWeight: 700, cursor: email.trim() && !codeSending ? 'pointer' : 'not-allowed' }}>
                      {codeSending ? (S().setup.codeSending || 'Отправка...') : S().setup.confirmEmail}
                    </button>
                  )}
                </div>
                {vStep === 1 && !emailVerified && (
                  <div className="anim-tx" style={{ display: 'flex', gap: 8, marginTop: 10, padding: 12, background: `${t.accentOrange}10`, borderRadius: 10, border: `1px dashed ${t.accentOrange}` }}>
                    <input placeholder={S().setup.codePlaceholder} value={code} onChange={e => setCode(e.target.value)} style={{ flex: 1, fontSize: 13, padding: '8px 12px' }} />
                    <Btn variant="success" onClick={doVerify}>{S().common.check}</Btn>
                    <Btn variant="ghost" onClick={() => setVStep(0)}><X size={16} /></Btn>
                  </div>
                )}
              </div>

              <div>
                <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 6, display: 'block' }}>{S().setup.phoneLabel} *</label>
                <input
                  inputMode="tel"
                  value={phone}
                  onChange={e => setPhone(phoneDigits(e.target.value))}
                  placeholder={S().setup.phonePlaceholder}
                  style={{ width: '100%', ...errBorder(!isValidPhone(phone)) }}
                />
                <div style={{ fontSize: 11, color: t.textMuted, marginTop: 4 }}>
                  {phone ? formatPhone(phone) : S().setup.phoneHint}
                </div>
                {touched && phone && !isValidPhone(phone) && (
                  <div style={{ fontSize: 11, color: t.accentRed, marginTop: 4 }}>{S().setup.phoneMinDigits}</div>
                )}
              </div>
            </>}
          </div>
        )}

        {/* ══════════ STEP 2: ACCOUNTS ══════════ */}
        {step === 2 && (
          <div>
            {/* Examples hint */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, padding: '10px 14px', background: `${t.accentOrange}10`, borderRadius: 10, border: `1px solid ${t.accentOrange}30` }}>
              <span style={{ fontSize: 18 }}>💡</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: t.accentOrange }}>{S().setup.examplesTitle}</div>
                <div style={{ fontSize: 12, color: t.textSec }}>{S().setup.step2Examples}</div>
              </div>
              <HelpTooltip text={HELP_TEXTS.setupAccount} size={15} />
            </div>

            {accs.map((a, i) => {
              const isDupName = a.name.trim() && accs.some((b, j) => j !== i && b.name.trim().toLowerCase() === a.name.trim().toLowerCase());
              const isDupIban = a.iban.trim() && accs.some((b, j) => j !== i && b.iban.trim().toLowerCase() === a.iban.trim().toLowerCase());
              return (
              <div key={a.id} style={{ marginBottom: 14, padding: '12px 14px', background: t.bgInput, borderRadius: 12, border: `1px solid ${t.border}` }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: `${t.accent}15`, color: t.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0 }}>{i + 1}</div>
                  <input value={a.name} onChange={e => { const v = [...accs]; v[i] = { ...v[i], name: e.target.value }; setAccs(v) }} placeholder={i === 0 ? S().setup.accountExample : S().setup.accountLabel(i + 1)} style={{ flex: 1, ...errBorder(!a.name.trim()), ...(touched && isDupName ? { border: `1.5px solid ${t.accentRed}` } : {}) }} />
                  <input type="color" value={a.color} onChange={e => { const v = [...accs]; v[i] = { ...v[i], color: e.target.value }; setAccs(v) }} style={{ width: 42, height: 42, padding: 2, borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgCard }} />
                  {accs.length > 1 && <button onClick={() => setAccs(accs.filter((_, j) => j !== i))} style={{ background: 'transparent', color: t.accentRed }}><Trash2 size={16} /></button>}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: isNarrow ? '1fr' : '1fr 1fr', gap: 8 }}>
                  <div>
                    <label style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, marginBottom: 4, display: 'block' }}>{S().setup.ibanLabel} *</label>
                    <input value={a.iban} onChange={e => { const v = [...accs]; v[i] = { ...v[i], iban: e.target.value }; setAccs(v) }} placeholder={S().setup.ibanPlaceholder} style={{ width: '100%', fontSize: 12, ...errBorder(!a.iban.trim()), ...(touched && isDupIban ? { border: `1.5px solid ${t.accentRed}` } : {}) }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, marginBottom: 4, display: 'block' }}>{S().setup.balanceLabel}</label>
                    <input type="number" value={a.balance} onChange={e => { const v = [...accs]; v[i] = { ...v[i], balance: e.target.value }; setAccs(v) }} placeholder={S().setup.balancePlaceholder} style={{ width: '100%', fontSize: 12 }} />
                  </div>
                </div>
              </div>
            )})}
            {touched && hasDupAccName && (
              <div style={{ padding: '10px 14px', background: `${t.accentRed}10`, border: `1px solid ${t.accentRed}40`, borderRadius: 10, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertCircle size={16} color={t.accentRed} style={{ flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: t.accentRed, fontWeight: 600 }}>{S().setup.duplicateAccountName}</span>
              </div>
            )}
            {touched && hasDupIban && (
              <div style={{ padding: '10px 14px', background: `${t.accentRed}10`, border: `1px solid ${t.accentRed}40`, borderRadius: 10, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertCircle size={16} color={t.accentRed} style={{ flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: t.accentRed, fontWeight: 600 }}>{S().setup.duplicateIban}</span>
              </div>
            )}
            <button onClick={() => setAccs([...accs, { id: uid(), name: '', iban: '', balance: '', color: COLORS[accs.length % COLORS.length], archived: false }])} style={{ background: 'transparent', color: t.accent, fontSize: 13, fontWeight: 700, marginTop: 8 }}><Plus size={14} /> {S().setup.addAccount}</button>

            {totalInitialBalance > 0 && (
              <div style={{ marginTop: 12, padding: '10px 12px', background: `${t.accentGreen}10`, borderRadius: 8, border: `1px solid ${t.accentGreen}30`, fontSize: 12, color: t.accentGreen, fontWeight: 600 }}>
                💡 {S().setup.balanceHint}
              </div>
            )}

            <div style={{ marginTop: 12, padding: '10px 12px', background: t.bgInput, borderRadius: 8, fontSize: 11, color: t.textMuted, lineHeight: 1.5 }}>
              {S().setup.step2Hint}
            </div>
          </div>
        )}

        {/* ══════════ STEP 3: FUNDS ══════════ */}
        {step === 3 && (
          <div>
            {/* Visual flow diagram */}
            <div style={{ padding: 16, background: `${t.accent}08`, borderRadius: 14, marginBottom: 16, border: `1px solid ${t.accent}20` }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: t.accent, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                {S().setup.step3FlowTitle}
                <HelpTooltip text={HELP_TEXTS.setupFundLink} size={15} />
              </div>

              {/* Flow visualization */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, flexWrap: 'wrap', padding: '10px 0' }}>
                <div style={{ padding: '8px 14px', background: `${t.accentGreen}20`, borderRadius: 10, border: `1px solid ${t.accentGreen}50`, textAlign: 'center' }}>
                  <div style={{ fontSize: 18, marginBottom: 2 }}>💰</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: t.accentGreen }}>{S().setup.flowClientPays}</div>
                  <div style={{ fontSize: 10, color: t.textSec }}>1 000 000 ₸</div>
                </div>
                <div style={{ fontSize: 18, color: t.accent }}>→</div>
                <div style={{ padding: '8px 14px', background: `${t.accent}20`, borderRadius: 10, border: `1px solid ${t.accent}50`, textAlign: 'center' }}>
                  <div style={{ fontSize: 18, marginBottom: 2 }}>💳</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: t.accent }}>{S().setup.flowYourAccount}</div>
                  <div style={{ fontSize: 10, color: t.textSec }}>Kaspi</div>
                </div>
                <div style={{ fontSize: 18, color: t.accent }}>→</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {funds.length > 0 ? funds.slice(0, 3).map((f, i) => (
                    <div key={i} style={{ padding: '4px 12px', background: `${f.color}20`, borderRadius: 8, border: `1px solid ${f.color}50`, display: 'flex', alignItems: 'center', gap: 6, minWidth: 120 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: f.color, flexShrink: 0 }} />
                      <span style={{ fontSize: 11, fontWeight: 600, color: t.text }}>{f.name || '...'}</span>
                      <span style={{ fontSize: 10, color: t.textMuted, marginLeft: 'auto' }}>{f.percent || '0'}%</span>
                    </div>
                  )) : (
                    <div style={{ padding: '6px 12px', background: t.bgInput, borderRadius: 8, fontSize: 11, color: t.textMuted }}>{S().setup.flowFundsNotConfigured}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Example text */}
            <div style={{ fontSize: 12, color: t.textSec, marginBottom: 14, padding: '10px 12px', background: `${t.accentGreen}08`, borderRadius: 8, border: `1px solid ${t.accentGreen}20`, lineHeight: 1.5 }}>
              💡 {S().setup.step3Example}
            </div>

            {funds.length > 0 && <div style={{ fontSize: 12, color: tPct === 100 ? t.accentGreen : (pctInvalid ? t.accentRed : t.textMuted), fontWeight: pctInvalid ? 700 : 400, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>{S().setup.percentHint(tPct)}<HelpTooltip text={HELP_TEXTS.setupFund} size={14} /></div>}
            {pctInvalid && (
              <div style={{ padding: '10px 14px', background: `${t.accentRed}10`, border: `1px solid ${t.accentRed}40`, borderRadius: 10, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertCircle size={16} color={t.accentRed} style={{ flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: t.accentRed, fontWeight: 600 }}>{S().setup.fundsPctError || 'Сумма процентов всех фондов должна быть ровно 100%. Исправьте значения, чтобы продолжить.'}</span>
              </div>
            )}
            {touched && hasDupFundName && (
              <div style={{ padding: '10px 14px', background: `${t.accentRed}10`, border: `1px solid ${t.accentRed}40`, borderRadius: 10, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertCircle size={16} color={t.accentRed} style={{ flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: t.accentRed, fontWeight: 600 }}>{S().setup.duplicateFundName}</span>
              </div>
            )}
            {funds.map((f, i) => {
              const pctVal = parseFloat(f.percent) || 0;
              const fundAmt = totalInitialBalance * pctVal / 100;
              const isDupFund = f.name.trim() && funds.some((g, j) => j !== i && g.name.trim().toLowerCase() === f.name.trim().toLowerCase());
              return isNarrow ? (
              <div key={f.id} style={{ marginBottom: 10, padding: '10px 10px', background: t.bgInput, borderRadius: 10, border: `1px solid ${t.border}` }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: f.color, flexShrink: 0 }} />
                  <input value={f.name} onChange={e => { const v = [...funds]; v[i] = { ...v[i], name: e.target.value }; setFunds(v) }} placeholder={S().setup.fundNamePlaceholder} style={{ flex: 1, ...errBorder(!f.name.trim()), ...(touched && isDupFund ? { border: `1.5px solid ${t.accentRed}` } : {}) }} />
                  <button onClick={() => setFunds(funds.filter((_, j) => j !== i))} style={{ background: 'transparent', color: t.accentRed, flexShrink: 0 }}><Trash2 size={16} /></button>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                    <input type="number" value={f.percent} onChange={e => { const v = [...funds]; v[i] = { ...v[i], percent: e.target.value }; setFunds(v) }} placeholder="0" style={{ width: 70, paddingRight: 24, textAlign: 'right', ...errBorder(!f.percent || pctVal <= 0), ...pctBorder }} />
                    <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 13, fontWeight: 700, color: t.textMuted, pointerEvents: 'none' }}>%</span>
                  </div>
                  {totalInitialBalance > 0 && pctVal > 0 && (
                    <span style={{ fontSize: 12, fontWeight: 700, color: t.accentGreen, flex: 1, textAlign: 'right' }}>{fmt(fundAmt)}</span>
                  )}
                  <input type="color" value={f.color} onChange={e => { const v = [...funds]; v[i] = { ...v[i], color: e.target.value }; setFunds(v) }} style={{ width: 36, height: 36, padding: 2, borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgCard, flexShrink: 0 }} />
                </div>
              </div>
              ) : (
              <div key={f.id} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: f.color, flexShrink: 0 }} />
                <input value={f.name} onChange={e => { const v = [...funds]; v[i] = { ...v[i], name: e.target.value }; setFunds(v) }} placeholder={S().setup.fundNamePlaceholder} style={{ flex: 1, minWidth: 0, ...errBorder(!f.name.trim()), ...(touched && isDupFund ? { border: `1.5px solid ${t.accentRed}` } : {}) }} />
                <div style={{ display: 'flex', alignItems: 'center', position: 'relative', flexShrink: 0 }}>
                  <input type="number" value={f.percent} onChange={e => { const v = [...funds]; v[i] = { ...v[i], percent: e.target.value }; setFunds(v) }} placeholder="0" style={{ width: 80, paddingRight: 26, textAlign: 'right', ...errBorder(!f.percent || pctVal <= 0), ...pctBorder }} />
                  <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13, fontWeight: 700, color: t.textMuted, pointerEvents: 'none' }}>%</span>
                </div>
                {totalInitialBalance > 0 && pctVal > 0 && (
                  <span style={{ fontSize: 12, fontWeight: 700, color: t.accentGreen, minWidth: 90, textAlign: 'right', flexShrink: 0 }}>{fmt(fundAmt)}</span>
                )}
                <input type="color" value={f.color} onChange={e => { const v = [...funds]; v[i] = { ...v[i], color: e.target.value }; setFunds(v) }} style={{ width: 42, height: 42, padding: 2, borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgInput, flexShrink: 0 }} />
                <button onClick={() => setFunds(funds.filter((_, j) => j !== i))} style={{ background: 'transparent', color: t.accentRed, flexShrink: 0 }}><Trash2 size={16} /></button>
              </div>
              );
            })}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
              <button onClick={() => setFunds([...funds, { id: uid(), name: '', percent: '', color: COLORS[(funds.length + 2) % COLORS.length], archived: false }])} style={{ background: 'transparent', color: t.accent, fontSize: 13, fontWeight: 700 }}><Plus size={14} /> {S().setup.addFund}</button>
              {funds.length > 0 && <button onClick={() => setFunds([])} style={{ background: 'transparent', color: t.textMuted, fontSize: 13, fontWeight: 600 }}>{S().setup.removeAllFunds}</button>}
            </div>
          </div>
        )}

        {/* ══════════ STEP 4: SUMMARY ══════════ */}
        {step === 4 && (
          <div style={{ display: 'grid', gap: 16 }}>
            <div style={{ padding: 16, background: t.bgInput, borderRadius: 12, border: `1px solid ${t.border}` }}>
              <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 6, color: t.text }}>{orgN}</div>
              <div style={{ fontSize: 13, color: t.textSec, lineHeight: 1.6 }}>
                {S().setup.ownerLabel} <strong style={{ color: t.text }}>{aN} ({aL})</strong><br />
                {S().setup.emailLabelShort} <strong style={{ color: t.text }}>{email}</strong><br />
                {S().setup.accountsCount} <strong style={{ color: t.text }}>{accs.length}</strong> · {S().setup.fundsCount} <strong style={{ color: t.text }}>{funds.length}</strong> {funds.length > 0 ? `(${tPct}%)` : ''}
                {totalInitialBalance > 0 && (<><br />{S().setup.balanceLabel.split('(')[0].trim()}: <strong style={{ color: t.accentGreen }}>{fmt(totalInitialBalance)}</strong></>)}
              </div>
              {accs.length > 0 && (
                <div style={{ marginTop: 10, display: 'grid', gap: 4 }}>
                  {accs.map((a, i) => (
                    <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: t.bgCard, borderRadius: 6, fontSize: 12 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: a.color, flexShrink: 0 }} />
                      <span style={{ fontWeight: 600, color: t.text }}>{a.name}</span>
                      <span style={{ color: t.textMuted, fontSize: 11 }}>IBAN: {a.iban}</span>
                      {parseFloat(a.balance) > 0 && <span style={{ marginLeft: 'auto', fontWeight: 700, color: t.accentGreen }}>{fmt(parseFloat(a.balance))}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* WHAT'S NEXT block */}
            <div style={{ padding: 16, background: `${t.accent}08`, borderRadius: 12, border: `1px solid ${t.accent}20` }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: t.accent, marginBottom: 10 }}>🚀 {S().setup.step4WhatsNext}</div>
              <div style={{ display: 'grid', gap: 8 }}>
                {[S().setup.step4Next1, S().setup.step4Next2, S().setup.step4Next3].map((txt, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: t.bgCard, borderRadius: 8, border: `1px solid ${t.border}` }}>
                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: `${t.accent}20`, color: t.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0 }}>{i + 1}</div>
                    <div style={{ fontSize: 13, color: t.text }}>{txt}</div>
                  </div>
                ))}
              </div>
            </div>

            <label style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              gap: 14, 
              cursor: 'pointer', 
              padding: '16px', 
              background: agreed ? `${t.accentGreen}08` : `${t.accentRed}05`, 
              borderRadius: 12, 
              border: `1px solid ${agreed ? t.accentGreen : t.border}`, 
              transition: 'all 0.2s ease', 
              width: '100%',
              boxSizing: 'border-box'
            }}>
              <input 
                type="checkbox" 
                checked={agreed} 
                onChange={e => setAgreed(e.target.checked)} 
                style={{ marginTop: 3, width: 17, height: 17, cursor: 'pointer', flexShrink: 0 }} 
              />
              <div style={{ fontSize: 13, color: t.text, lineHeight: 1.5, flex: 1, userSelect: 'none' }}>
                {S().setup.agreementText} <span onClick={(e) => { e.preventDefault(); setDocToShow('tos') }} style={{ color: t.accent, textDecoration: 'underline', fontWeight: 600 }}>{S().setup.tosLink}</span>, <span onClick={(e) => { e.preventDefault(); setDocToShow('privacy') }} style={{ color: t.accent, textDecoration: 'underline', fontWeight: 600 }}>{S().setup.privacyLink}</span>, <span onClick={(e) => { e.preventDefault(); setDocToShow('consent') }} style={{ color: t.accent, textDecoration: 'underline', fontWeight: 600 }}>{S().setup.consentLink}</span>.
              </div>
            </label>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 28 }}>
          <button onClick={step === 1 ? onCancel : () => { setTouched(false); setStep(step - 1) }} style={{ background: 'transparent', color: t.textSec, fontSize: 14, fontWeight: 700 }}>{step === 1 ? S().common.cancel : S().common.back}</button>
          {step < 4 ? (
            <Btn variant="primary" onClick={() => {
              const valid = step === 1 ? ok1 : step === 2 ? ok2 : ok3;
              if (!valid) { setTouched(true); return; }
              if (step === 3 && funds.length === 0 && !window.confirm(S().setup.noFundsConfirm)) return;
              setTouched(false); setStep(step + 1);
            }} style={{ padding: '10px 20px', fontSize: 14 }}>{step === 3 && funds.length === 0 ? S().setup.skipFunds : S().common.next}</Btn>
          ) : <Btn variant="success" onClick={finish} disabled={!agreed} style={{ opacity: agreed ? 1 : 0.5, padding: '10px 20px', fontSize: 14 }}><Check size={16} />{S().setup.finishSetup}</Btn>}
        </div>
        <LangThemeBar changeLang={changeLang} toggle={toggle} meta={meta} />
      </div>
    </div>
  );
}

function LoginScreen({ org, onLogin, onBack, toggle, tn, changeLang, meta }) {
  const t = useTheme();
  const [login, setLogin] = useState('');
  const [pass, setPass] = useState('');
  const [err, setErr] = useState('');
  const [show, setShow] = useState(false);
  const go = () => { const u = org.users.find(u => u.login === login && u.password === pass); if (u) onLogin(u); else setErr(S().login.wrongCredentials) };
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: t.gradient }}>
      <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 16, padding: '32px 28px', width: 370, maxWidth: '90vw', boxShadow: t.shadow }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}><div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 2, color: t.accent }}>{S().home.brand}</div><div style={{ fontSize: 15, fontWeight: 600, marginTop: 8 }}>{org.name}</div></div>
        <input value={login} onChange={e => setLogin(e.target.value)} placeholder={S().login.loginPlaceholder} onKeyDown={e => e.key === 'Enter' && go()} style={{ marginBottom: 14 }} />
        {/* Логин: НЕ фильтруем кириллицу — legacy-юзеры с кириллическими паролями
            должны иметь возможность залогиниться (бэк делает только bcrypt.compare). */}
        <div style={{ position: 'relative', marginBottom: 20 }}><input type={show ? 'text' : 'password'} value={pass} onChange={e => setPass(e.target.value)} placeholder={S().login.passwordPlaceholder} onKeyDown={e => e.key === 'Enter' && go()} /><button onClick={() => setShow(!show)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'transparent', color: t.textMuted }}>{show ? <EyeOff size={18} /> : <Eye size={18} />}</button></div>
        {err && <div style={{ color: t.accentRed, fontSize: 13, marginBottom: 14 }}><AlertCircle size={14} /> {err}</div>}
        <button onClick={go} style={{ width: '100%', padding: 12, borderRadius: 10, background: t.accent, color: t.btnText, fontWeight: 700, fontSize: 15 }}>{S().login.enter}</button>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}><button onClick={onBack} style={{ background: 'transparent', color: t.textMuted, fontSize: 12 }}>← {S().common.back}</button></div>
        <LangThemeBar changeLang={changeLang} toggle={toggle} meta={meta} />
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// TwoFAScreen (Stage 11)
// Второй экран логина для юзеров с включённой 2FA. Получает tempToken
// из onPending2FA коллбэка после /login. На этом экране юзер вводит
// 6-значный TOTP-код или резервный код XXXX-XXXX.
// При TEMP_TOKEN_INVALID (5+ мин с момента /login) — показывает ошибку
// и через 2.2с возвращает на экран логина.
// ════════════════════════════════════════════════════════════════
function TwoFAScreen({ tempToken, onSuccess, onBack, toggle, changeLang, meta }) {
  const t = useTheme();
  const T = S().login.twoFA;
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Точные форматы из бэкенда (server/routes/auth.js):
  //   isTotpCodeFormat:    /^\d{6}$/
  //   isBackupCodeFormat:  /^[A-F0-9]{4}-?[A-F0-9]{4}$/i  ← дефис опционален (длина 8 или 9)
  // Аудит-фикс: раньше canSubmit принимал только length 6 или 9 — 8-символьный
  // backup-код без дефиса (например "AB12CD34") блокировался, хотя бэк его принимает.
  const trimmed = code.trim();
  const isTotp = /^\d{6}$/.test(trimmed);
  const isBackup = /^[A-F0-9]{4}-?[A-F0-9]{4}$/i.test(trimmed);
  const canSubmit = !busy && (isTotp || isBackup);

  const verify = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError('');
    try {
      const data = await api.verify2FA(tempToken, trimmed.toUpperCase());
      // Backup-код был использован — предупреждаем юзера если их остаётся <= 2.
      if (data && data.usedBackup) {
        const remaining = data.backupCodesRemaining || 0;
        if (remaining <= 2) {
          alert(T.backupCodesLow(remaining));
        }
      }
      onSuccess(data.user);
    } catch (e) {
      // tempToken истёк (TTL 5 мин) — отправляем юзера обратно на /login
      if (e && (e.code === 'TEMP_TOKEN_INVALID' || e.status === 401)) {
        if (e.code === 'TEMP_TOKEN_INVALID') {
          setError(T.expired);
          setTimeout(() => onBack(), 2200);
          return;
        }
        setError(e.message || T.invalidCode);
      } else {
        setError(e?.message || T.invalidCode);
      }
      setBusy(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: t.gradient, padding: 16 }}>
      <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 16, padding: '32px 28px', width: 400, maxWidth: '100%', boxShadow: t.shadow }}>
        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <div style={{ width: 56, height: 56, borderRadius: 14, background: `${t.accent}15`, color: t.accent, margin: '0 auto 12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ShieldCheck size={28} />
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: t.text }}>{T.title}</div>
          <div style={{ fontSize: 13, color: t.textMuted, marginTop: 8, lineHeight: 1.5 }}>{T.desc}</div>
        </div>

        <input
          type="text"
          inputMode="text"
          autoFocus
          autoComplete="one-time-code"
          value={code}
          onChange={e => setCode(e.target.value.toUpperCase().replace(/\s/g, ''))}
          onKeyDown={e => { if (e.key === 'Enter') verify(); }}
          placeholder={T.codePlaceholder}
          maxLength={11}
          style={{ width: '100%', padding: '14px 16px', fontSize: 18, textAlign: 'center', letterSpacing: 4, fontFamily: 'monospace', fontWeight: 700, marginBottom: 14 }}
          disabled={busy}
        />

        {error && (
          <div style={{ color: t.accentRed, fontSize: 13, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
            <AlertCircle size={14} /> {error}
          </div>
        )}

        <button
          onClick={verify}
          disabled={!canSubmit}
          style={{ width: '100%', padding: 12, borderRadius: 10, background: t.accent, color: t.btnText, fontWeight: 700, fontSize: 15, opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? 'pointer' : 'not-allowed', border: 'none' }}
        >
          {busy ? T.verifying : T.verifyBtn}
        </button>

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
          <button onClick={onBack} disabled={busy} style={{ background: 'transparent', color: t.textMuted, fontSize: 12, cursor: busy ? 'not-allowed' : 'pointer', border: 'none' }}>
            {T.backToLogin}
          </button>
        </div>

        <LangThemeBar changeLang={changeLang} toggle={toggle} meta={meta} />
      </div>
    </div>
  );
}

// Phase 9.3: индикатор синхронизации в sidebar.
// lastSyncedAt — Date.now() последнего успешного контакта с сервером.
// Тикает раз в секунду через setInterval + локальный counter (ребиндит при необходимости).
// Цветовое кодирование:
//   < 60 сек — t.textMuted (нормально, всё свежее)
//   60–120 сек — t.textSec (между poll-циклами, нормально)
//   > 120 сек — t.accentRed (предупреждение — связь возможно потеряна)
function SyncIndicator({ lastSyncedAt }) {
  const t = useTheme();
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick(n => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  if (!lastSyncedAt) return null;
  const T = S().sidebar.sync || {};
  let label = null;
  let color = t.textMuted;
  if (lastSyncedAt) {
    const ageSec = Math.max(0, Math.floor((Date.now() - lastSyncedAt) / 1000));
    if (ageSec < 5) label = T.justNow || 'только что';
    else if (ageSec < 60) label = (T.secondsAgo || 'N сек назад').replace('N', ageSec);
    else if (ageSec < 3600) label = (T.minutesAgo || 'N мин назад').replace('N', Math.floor(ageSec / 60));
    else label = (T.hoursAgo || 'N ч назад').replace('N', Math.floor(ageSec / 3600));
    color = ageSec > 120 ? t.accentRed : t.textMuted;
  }
  const prefix = T.updated || 'Обновлено';
  return (
    <div style={{ marginTop: 2 }}>
      {label && (
        <div style={{ fontSize: 10, color, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {prefix} {label}
        </div>
      )}
      {/* Phase A (2026-05-26): pending sync badge удалён вместе с outbox. */}
    </div>
  );
}

function MainApp({ data, uOrg, user, perms, meta, uMeta, globalUser, setGlobalUser, userOrgs, orgId, switchOrg, onLogout, onHome, onAddOrg, toggle, tn, changeLang, onDeleteOrg, triggerSyncCheck, lastSyncedAt }) {
  const currentCur = CURRENCIES.find(c => c.id === (data.currency || 'KZT'));
  setCurrencySymbol(currentCur ? currentCur.symbol : '₸');
  const apiSync = useApiSync(orgId);

  // Stage 21.5 (2026-05-14): зарегистрировать apiSync в module-level ref shared.js
  // чтобы addAuditLog мог вызывать syncCreateAuditEntry без изменения signature
  // (32 callsites не трогаем). Cleanup на unmount.
  useEffect(() => {
    _setApiSyncRef(apiSync);
    return () => _setApiSyncRef(null);
  }, [apiSync]);

  const t = useTheme();
  const [tab, setTab] = useState('af');

  // Stage 4.0 / Phase 9.1: при смене вкладки sidebar — попросить sync manager
  // проверить актуальную версию orgData. throttle защищает от spam.
  useEffect(() => {
    if (typeof triggerSyncCheck === 'function') triggerSyncCheck('tab-change:' + tab);
  }, [tab, triggerSyncCheck]);

  const [navTx, setNavTx] = useState(null);
  // Task #42 (2026-05-21): deep-link с дашборда AFTab в конкретный долг.
  // Юзер кликает иконку-ручку или строку контрагента на главной → автоматически
  // переключается на вкладку «Обязательства» и открывается ViewDebtModal этого долга.
  // Тот же паттерн что navTx → TxTab — single-shot, consumer (DebtsTab) очищает после применения.
  const [navDebt, setNavDebt] = useState(null);
  const [supportUnread, setSupportUnread] = useState(0);
  const [appDocToShow, setAppDocToShow] = useState(null);
  const [showTour, setShowTour] = useState(() => {
    if (!user) return false;
    const key = `inmonex_tour_${data.id}_${user.id}`;
    if (localStorage.getItem(key)) return false;
    localStorage.setItem(key, '1');
    return true;
  });

  // ─── Подписка ───
  const [subStatus, setSubStatus] = useState(null);
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const isSubExpired = subStatus && (subStatus.subscriptionStatus === 'expired' || subStatus.subscriptionStatus === 'free');

  useEffect(() => {
    api.getSubscriptionStatus().then(s => {
      setSubStatus(s);
      if (s && (s.subscriptionStatus === 'expired' || s.subscriptionStatus === 'free')) setShowExpiredModal(true);
    }).catch(e => console.warn('[SYNC]', e));
  }, []);

  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1440);
  const isCompact = windowWidth < 980;
  const [sidebarOpen, setSidebarOpen] = useState(!isCompact);

  useEffect(() => { const h = () => setWindowWidth(window.innerWidth); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h) }, []);
  useEffect(() => { if (isCompact) setSidebarOpen(false); else setSidebarOpen(true) }, [isCompact]);

  // ─── Бейдж непрочитанных тикетов ───
  useEffect(() => {
    if (!api.getToken()) return;
    const fetchCount = () => {
      api.getUnreadTicketCount().then(d => setSupportUnread(d.count || 0)).catch(() => {});
    };
    fetchCount();
    const iv = setInterval(fetchCount, 30000);
    return () => clearInterval(iv);
  }, []);

  // При переходе на support — обнулить и перезапросить через 2с
  useEffect(() => {
    if (tab === 'support') {
      setSupportUnread(0);
      const tm = setTimeout(() => {
        if (api.getToken()) api.getUnreadTicketCount().then(d => setSupportUnread(d.count || 0)).catch(() => {});
      }, 2000);
      return () => clearTimeout(tm);
    }
  }, [tab]);

  const navigateToTx = (txId, filterId) => { setNavTx({ txId, filterId }); setTab('tx'); if (isCompact) setSidebarOpen(false) };
  // Task #42: navigate to specific debt — переход на /debts с открытым ViewDebtModal.
  const navigateToDebt = (debtId) => { setNavDebt(debtId); setTab('debts'); if (isCompact) setSidebarOpen(false); };

  const tabs = [
    { id: 'af', label: S().sidebar.tabs.af, icon: Layers },
    { id: 'data', label: S().sidebar.tabs.data, icon: BookMarked },
    { id: 'debts', label: S().sidebar.tabs.debts, icon: ClipboardList },
    { id: 'ops', label: S().sidebar.tabs.ops, icon: ArrowDownCircle },
    { id: 'tx', label: S().sidebar.tabs.tx, icon: BarChart3 },
    { id: 'reports', label: S().sidebar.tabs.reports, icon: TrendingUp }
  ];
  // История — только для owner (видит всё). Admin/accountant имеют seeHistory='own' в ROLE_PERMS,
  // но по политике компании историю операций видит только владелец — это его аудит-trail.
  if (perms.seeHistory === 'all') tabs.push({ id: 'history', label: S().sidebar.tabs.history, icon: History });
  if (userOrgs.length > 1) tabs.push({ id: 'multiorg', label: S().sidebar.tabs.multiorg, icon: Building2 });
  if (perms.all) tabs.push({ id: 'admin', label: S().sidebar.tabs.admin, icon: Settings });
  // Этап 2: "Мой аккаунт" доступен ВСЕМ ролям. Каждый юзер (owner/admin/accountant/viewer)
  // должен иметь UI для своих личных данных, 2FA, сессий и security events.
  tabs.push({ id: 'myaccount', label: S().sidebar.tabs.myaccount, icon: UserCheck });
  tabs.push({ id: 'support', label: S().sidebar?.tabs?.support || 'Поддержка', icon: MessageSquare, badge: supportUnread });
  tabs.push({ id: 'referral', label: S().sidebar?.tabs?.referral || 'Рефералы', icon: Gift });
  if (perms.all) tabs.push({ id: 'subscription', label: S().sidebar.tabs.subscription, icon: Crown });

  const aAccs = data.accounts.filter(a => !a.archived);
  const aFunds = data.funds.filter(f => !f.archived);
  // На мобильных: сайдбар скрыт полностью, открывается как overlay на 240px
  // На десктопе: сворачивается до 70px иконок
  const sw = isCompact ? (sidebarOpen ? 240 : 0) : (sidebarOpen ? 240 : 70);

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>

      {appDocToShow && <LegalDocsModal doc={appDocToShow} onClose={() => setAppDocToShow(null)} />}

      {showTour && <WelcomeTour onClose={() => setShowTour(false)} onNavigate={(tabId) => { setTab(tabId); setShowTour(false); if (isCompact) setSidebarOpen(false); }} />}

      {/* Модалка «Подписка истекла» */}
      {showExpiredModal && isSubExpired && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: t.bgCard, borderRadius: 16, padding: 32, maxWidth: 420, width: '90%', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>
            <div style={{ width: 56, height: 56, borderRadius: 16, background: `${t.accentRed}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <AlertCircle size={28} color={t.accentRed} />
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: t.text, marginBottom: 8 }}>{S().subscription.modalTitle}</div>
            <div style={{ fontSize: 14, color: t.textSec, lineHeight: 1.6, marginBottom: 24 }}>{S().subscription.modalText}</div>
            <button onClick={() => { setShowExpiredModal(false); setTab('subscription'); }} style={{ width: '100%', padding: '12px 24px', borderRadius: 10, background: t.accent, color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', marginBottom: 10 }}>
              <Crown size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />{S().subscription.modalBtn}
            </button>
            <button onClick={() => setShowExpiredModal(false)} style={{ background: 'transparent', color: t.textMuted, fontSize: 13, cursor: 'pointer', padding: 8 }}>
              {S().common.close || 'Закрыть'}
            </button>
          </div>
        </div>
      )}
      {isCompact && sidebarOpen && <div onClick={() => setSidebarOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 15 }} />}

      {/* САЙДБАР */}
      <div style={{ width: isCompact ? 240 : sw, background: t.bgCard, borderRight: `1.5px solid ${t.border}`, display: (isCompact && !sidebarOpen) ? 'none' : 'flex', flexDirection: 'column', height: '100vh', boxShadow: t.shadow, zIndex: 16, flexShrink: 0, transition: 'width .2s', position: isCompact ? 'fixed' : 'relative' }}>
        <div style={{ padding: sidebarOpen ? '12px 16px' : '12px 8px', borderBottom: `1.5px solid ${t.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: sidebarOpen ? 'space-between' : 'center', gap: 8 }}>
            <div style={{ overflow: 'hidden' }}>
              <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 3, color: t.accent, textAlign: sidebarOpen ? 'left' : 'center' }}>INMONEX</div>
            </div>
            <button onClick={() => setSidebarOpen(v => !v)} style={{ background: t.bgInput, color: t.textSec, borderRadius: 8, padding: '5px 7px', fontSize: 14, flexShrink: 0 }}>{sidebarOpen ? '◁' : '▷'}</button>
          </div>
          {sidebarOpen && (
            <div style={{ marginTop: 8 }}>
              {userOrgs.length <= 1 && <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{data.name}</div>}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4, gap: 6 }}>
                <div style={{ fontSize: 11, color: t.textMuted, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name} <span style={{ opacity: 0.6 }}>({ROLE_LABELS[user.role]})</span></div>
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                  {userOrgs.length > 1 && <button onClick={onHome} title={S().sidebar.allOrgs} style={{ background: 'transparent', color: t.textMuted, fontSize: 11, padding: 2 }}><Building2 size={12} /></button>}
                  <button onClick={onLogout} title={S().sidebar.logout} style={{ background: 'transparent', color: t.accentRed, fontSize: 11, padding: 2 }}><LogOut size={12} /></button>
                </div>
              </div>
              {/* Phase 9.3: индикатор синхронизации */}
              <SyncIndicator lastSyncedAt={lastSyncedAt} />
            </div>
          )}
        </div>

        {/* ПЕРЕКЛЮЧАТЕЛЬ ОРГАНИЗАЦИЙ (выпадающий список) */}
        {userOrgs.length > 1 && (
          <div style={{ padding: sidebarOpen ? '8px 10px' : '8px 4px', borderBottom: `1.5px solid ${t.border}` }}>
            {sidebarOpen ? (
              <div style={{ position: 'relative' }}>
                <select
                  value={orgId}
                  onChange={e => switchOrg(e.target.value)}
                  style={{
                    width: '100%', padding: '8px 32px 8px 34px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                    background: t.bgInput, color: t.text, border: `1.5px solid ${t.accent}40`,
                    appearance: 'none', cursor: 'pointer',
                  }}
                >
                  {userOrgs.map(o => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
                <div style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, borderRadius: 4, background: (userOrgs.find(o => o.id === orgId)?.color) || t.accent, pointerEvents: 'none' }} />
                <ChevronRight size={14} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%) rotate(90deg)', color: t.textMuted, pointerEvents: 'none' }} />
              </div>
            ) : (
              (() => {
                const currentOrg = userOrgs.find(o => o.id === orgId);
                const currentIdx = userOrgs.indexOf(currentOrg);
                const nextOrg = userOrgs[(currentIdx + 1) % userOrgs.length];
                return (
                  <button onClick={() => switchOrg(nextOrg.id)} title={S().setup.switchTo(nextOrg.name)} style={{
                    width: '100%', display: 'flex', justifyContent: 'center', padding: 4, borderRadius: 8, background: 'transparent', cursor: 'pointer',
                  }}>
                    <div style={{ width: 28, height: 28, borderRadius: 6, background: (currentOrg?.color) || t.accent, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800 }}>
                      {currentOrg?.name.charAt(0).toUpperCase()}
                    </div>
                  </button>
                );
              })()
            )}
          </div>
        )}

        <nav style={{ flex: 1, padding: '10px 6px', overflowY: 'auto' }}>
          {tabs.map(tb => {
            const Icon = tb.icon;
            const active = tab === tb.id;
            return (
              <button key={tb.id} onClick={() => { setTab(tb.id); if (tb.id !== 'tx') setNavTx(null); if (isCompact) setSidebarOpen(false) }} title={tb.label} style={{ display: 'flex', alignItems: 'center', justifyContent: sidebarOpen ? 'flex-start' : 'center', gap: 9, width: '100%', padding: '10px 14px', borderRadius: 10, marginBottom: 3, background: active ? (tn === 'dark' ? '#1e2740' : '#e0e7ff') : 'transparent', color: active ? t.accent : t.textSec, fontSize: 14, fontWeight: active ? 700 : 400, position: 'relative' }}>
                <Icon size={17} />{sidebarOpen && tb.label}
                {sidebarOpen && tb.badge > 0 && <span style={{ marginLeft: 'auto', background: '#ef4444', color: '#fff', fontSize: 11, fontWeight: 700, borderRadius: 10, padding: '1px 7px', minWidth: 18, textAlign: 'center', lineHeight: '18px' }}>{tb.badge}</span>}
                {!sidebarOpen && tb.badge > 0 && <span style={{ position: 'absolute', top: 6, right: 6, width: 8, height: 8, borderRadius: '50%', background: '#ef4444' }} />}
              </button>
            )
          })}
          {user.role === 'owner' && (
            <button onClick={() => { onAddOrg(); if (isCompact) setSidebarOpen(false) }} title={S().sidebar.addOrg} style={{ display: 'flex', alignItems: 'center', justifyContent: sidebarOpen ? 'flex-start' : 'center', gap: 9, width: '100%', padding: '10px 14px', borderRadius: 10, marginTop: 8, borderTop: `1px solid ${t.border}`, paddingTop: 14, background: 'transparent', color: t.accentGreen || t.accent, fontSize: 13, fontWeight: 600 }}>
              <Plus size={17} />{sidebarOpen && S().sidebar.addOrg}
            </button>
          )}
        </nav>

        <div style={{ padding: sidebarOpen ? '8px 12px' : '8px', borderTop: `1.5px solid ${t.border}` }}>
          {/* Язык + Тема — одна строка */}
          {sidebarOpen ? (
            <div style={{ display: 'flex', gap: 4, marginBottom: 6, alignItems: 'center' }}>
              <div style={{ display: 'flex', flex: 1, borderRadius: 8, overflow: 'hidden', border: `1px solid ${t.accent}25` }}>
                {LANGS.map(l => { const cur = getLang(); return (
                  <button key={l.id} onClick={() => changeLang(l.id)} style={{ flex: 1, padding: '4px 0', fontSize: 10, fontWeight: cur === l.id ? 700 : 500, background: cur === l.id ? `${t.accent}18` : 'transparent', color: cur === l.id ? t.accent : t.textMuted, borderRight: l.id !== 'en' ? `1px solid ${t.accent}15` : 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                    <span style={{ fontSize: 11 }}>{l.flag}</span> {l.short}
                  </button>
                )})}
              </div>
              <button onClick={toggle} title={tn === 'dark' ? S().home.light : S().home.dark} style={{ background: t.bgInput, color: t.textMuted, borderRadius: 8, padding: '4px 8px', fontSize: 13, flexShrink: 0, cursor: 'pointer' }}>{tn === 'dark' ? <Sun size={13} /> : <Moon size={13} />}</button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <button onClick={() => { const ls = ['ru','kz','en']; const i = ls.indexOf(getLang()); changeLang(ls[(i+1)%3]) }} title="Language" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', padding: '6px', borderRadius: 8, background: t.bgInput, color: t.textSec, fontSize: 12, cursor: 'pointer' }}>🌐</button>
              <button onClick={toggle} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', padding: '6px', borderRadius: 8, background: t.bgInput, color: t.textSec, fontSize: 12, cursor: 'pointer' }}>{tn === 'dark' ? <Sun size={13} /> : <Moon size={13} />}</button>

            </div>
          )}
          {/* Юр.ссылки + копирайт — компактно */}
          {sidebarOpen && (
            <div style={{ fontSize: 9, color: t.textMuted, textAlign: 'center', lineHeight: 1.5 }}>
              <span onClick={() => { const lg = getLang(); window.open(`/legal/terms${lg === 'ru' ? '' : '_' + lg}.html`, '_blank') }} style={{ cursor: 'pointer', textDecoration: 'underline' }}>{S().sidebar.tosLink}</span>
              {' · '}
              <span onClick={() => { const lg = getLang(); window.open(`/legal/privacy${lg === 'ru' ? '' : '_' + lg}.html`, '_blank') }} style={{ cursor: 'pointer', textDecoration: 'underline' }}>{S().sidebar.privacyLink}</span>
              {' · '}
              <span onClick={() => { const lg = getLang(); window.open(`/legal/consent${lg === 'ru' ? '' : '_' + lg}.html`, '_blank') }} style={{ cursor: 'pointer', textDecoration: 'underline' }}>{S().sidebar.dataProcessingLink}</span>
              <div style={{ marginTop: 2, opacity: 0.6 }}>{S().sidebar.copyright(new Date().getFullYear())}</div>
            </div>
          )}
        </div>
      </div>

      {/* ПРАВАЯ ЧАСТЬ ПРИЛОЖЕНИЯ */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', marginLeft: 0, overflow: 'hidden' }}>

        {/* СТРОКА БАЛАНСОВ (Тикер) - На самом верху, без отступов */}
        {tab !== 'af' && tab !== 'admin' && tab !== 'multiorg' && tab !== 'subscription' && (
          <div style={{ flexShrink: 0, width: '100%', zIndex: 10 }}>
            <GlobalStats data={data} aAccs={aAccs} aFunds={aFunds} isCompact={isCompact} />
          </div>
        )}

        {/* РАБОЧАЯ ЗОНА (С прокруткой вниз, с отступами по краям) */}
        <div style={{ flex: 1, padding: isCompact ? '12px 10px' : '24px 32px', overflowY: 'auto' }}>

          {/* Баннер подписки */}
          {isSubExpired && (
            <div onClick={() => setTab('subscription')} style={{ padding: '10px 16px', background: `${t.accentRed}15`, border: `1px solid ${t.accentRed}40`, borderRadius: 10, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <AlertCircle size={18} color={t.accentRed} />
              <span style={{ fontSize: 13, fontWeight: 600, color: t.accentRed, flex: 1 }}>{S().subscription.expiredBanner}</span>
              <ChevronRight size={16} color={t.accentRed} />
            </div>
          )}
          {subStatus && (subStatus.subscriptionStatus === 'trial' || subStatus.subscriptionStatus === 'active') && subStatus.daysLeft > 0 && subStatus.daysLeft <= 7 && (
            <div onClick={() => setTab('subscription')} style={{ padding: '10px 16px', background: `${t.accent}10`, border: `1px solid ${t.accent}30`, borderRadius: 10, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <Crown size={18} color={t.accent} />
              <span style={{ fontSize: 13, fontWeight: 600, color: t.accent, flex: 1 }}>{S().subscription.expiringBanner(subStatus.daysLeft)}</span>
              <ChevronRight size={16} color={t.accent} />
            </div>
          )}

          {/* Кнопка меню для мобильных устройств */}
          {isCompact && (
            <div style={{ marginBottom: 20 }}>
              <button onClick={() => setSidebarOpen(v => !v)} style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10, padding: '8px 12px', color: t.textSec, display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 700, fontSize: 13 }}>
                {S().sidebar.menu}
              </button>
            </div>
          )}

          {/* Вкладки */}
          {tab === 'af' && <AFTab data={data} uOrg={uOrg} perms={perms} aAccs={aAccs} aFunds={aFunds} setTab={setTab} onNavigate={navigateToTx} onNavigateDebt={navigateToDebt} isCompact={isCompact} apiSync={apiSync} onFundsChanged={() => { try { window.dispatchEvent(new CustomEvent('inmonex-refetch-funds')); } catch (e) {} }} />}
          {tab === 'data' && <DataTab data={data} uOrg={uOrg} perms={perms} isCompact={isCompact} apiSync={apiSync} />}
          {tab === 'ops' && <OpsTab data={data} uOrg={uOrg} user={user} perms={perms} aAccs={aAccs} aFunds={aFunds} isCompact={isCompact} apiSync={apiSync} />}
          {tab === 'debts' && <DebtsTab data={data} uOrg={uOrg} user={user} perms={perms} isCompact={isCompact} apiSync={apiSync} orgId={orgId} navDebt={navDebt} onClearNavDebt={() => setNavDebt(null)} />}
          {tab === 'history' && <HistoryTab data={data} user={user} perms={perms} isCompact={isCompact} />}
          {tab === 'reports' && <ReportsTab data={data} aAccs={aAccs} aFunds={aFunds} isCompact={isCompact} />}
          {tab === 'multiorg' && <MultiOrgReportsTab userOrgs={userOrgs} meta={meta} isCompact={isCompact} />}
          {tab === 'admin' && <AdminTab data={data} uOrg={uOrg} user={user} perms={perms} isCompact={isCompact} meta={meta} uMeta={uMeta} globalUser={globalUser} setGlobalUser={setGlobalUser} orgId={orgId} changeLang={changeLang} apiSync={apiSync} onDeleteOrg={onDeleteOrg} />}
          {/* Этап 2: личный кабинет — для всех ролей. Роутинг без условия perms (доступ ВСЕМ). */}
          {tab === 'myaccount' && <MyAccountTab user={user} globalUser={globalUser} setGlobalUser={setGlobalUser} />}
          {tab === 'tx' && <TxTab data={data} uOrg={uOrg} user={user} perms={perms} navTx={navTx} onClearNav={() => setNavTx(null)} isCompact={isCompact} apiSync={apiSync} />}
          {tab === 'support' && <SupportTab />}
          {tab === 'referral' && <ReferralTab />}
          {tab === 'subscription' && perms.all && <SubscriptionTab globalUser={globalUser} userOrgs={userOrgs} isCompact={isCompact} />}
        </div>
      </div>
    </div>
  );
}

// ════════════ WELCOME TOUR ════════════

// ════════════ МОДАЛКА УДАЛЕНИЯ (организации / аккаунта) ════════════

function DeleteModal({ mode, orgId, orgName, onDeleteOrg, onDeleteAccount, onDownloadOrg, onDownloadFull, onClose }) {
  // mode: 'org' | 'account'
  const t = useTheme();
  const [step, setStep] = useState(1); // 1=confirm, 2=backup, 3=password
  const [backupDone, setBackupDone] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Stage 11: подтверждение паролем перед необратимым удалением
  const [password, setPassword] = useState('');
  const [pwError, setPwError] = useState('');

  const isOrg = mode === 'org';
  const confirmText = isOrg ? S().deletion.deleteOrgConfirm(orgName) : S().deletion.deleteAccountConfirm;
  const backupText = isOrg ? S().deletion.backupOrgText : S().deletion.backupAccountText;

  const handleBackup = () => {
    if (isOrg) onDownloadOrg(orgId);
    else onDownloadFull();
    setBackupDone(true);
  };

  const handleDelete = async () => {
    if (!password || password.length < 1) {
      setPwError('Введите пароль');
      return;
    }
    setPwError('');
    setDeleting(true);
    try {
      if (isOrg) await onDeleteOrg(orgId, password);
      else await onDeleteAccount(password);
    } catch (e) {
      // 401 PASSWORD_INVALID или другая ошибка — показываем юзеру, оставляем модалку
      const msg = (e && e.message) || 'Не удалось удалить';
      setPwError(msg);
      setDeleting(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: t.bgCard, borderRadius: 16, padding: 28, maxWidth: 440, width: '92%', boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>

        {/* Шаг 1: Подтверждение */}
        {step === 1 && (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>{S().deletion.step1of2}</div>
            <div style={{ width: 48, height: 48, borderRadius: 14, background: `${t.accentRed}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Trash2 size={24} color={t.accentRed} />
            </div>
            <div style={{ fontSize: 17, fontWeight: 800, color: t.text, textAlign: 'center', marginBottom: 10 }}>
              {isOrg ? S().deletion.deleteOrg : S().deletion.deleteAccount}
            </div>
            <div style={{ fontSize: 14, color: t.textSec, textAlign: 'center', lineHeight: 1.6, marginBottom: 24 }}>
              {confirmText}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={onClose} style={{ flex: 1, padding: '12px', borderRadius: 10, background: t.bgInput, color: t.textSec, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                {S().common.cancel}
              </button>
              <button onClick={() => setStep(2)} style={{ flex: 1, padding: '12px', borderRadius: 10, background: t.accentRed, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                {isOrg ? S().deletion.deleteOrg : S().deletion.deleteAccount}
              </button>
            </div>
          </>
        )}

        {/* Шаг 2: Бэкап */}
        {step === 2 && (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>{S().deletion.step2of2}</div>
            <div style={{ width: 48, height: 48, borderRadius: 14, background: `${t.accent}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <AlertCircle size={24} color={t.accent} />
            </div>
            <div style={{ fontSize: 17, fontWeight: 800, color: t.text, textAlign: 'center', marginBottom: 10 }}>
              {S().deletion.backupTitle}
            </div>
            <div style={{ fontSize: 14, color: t.textSec, textAlign: 'center', lineHeight: 1.6, marginBottom: 20 }}>
              {backupText}
            </div>

            {/* Кнопка скачать бэкап */}
            <button onClick={handleBackup} style={{
              width: '100%', padding: '14px', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', marginBottom: 10,
              background: backupDone ? `${t.accentGreen}15` : `${t.accent}12`,
              border: `2px solid ${backupDone ? t.accentGreen : t.accent}`,
              color: backupDone ? t.accentGreen : t.accent,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}>
              {backupDone ? <><Check size={16} /> {S().deletion.backupDownloaded}</> : <><Settings size={16} /> {S().deletion.downloadBackup}</>}
            </button>

            {/* Кнопки: переход к шагу 3 (ввод пароля) */}
            <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
              <button onClick={onClose} style={{ flex: 1, padding: '12px', borderRadius: 10, background: t.bgInput, color: t.textSec, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                {S().common.cancel}
              </button>
              <button onClick={() => setStep(3)} style={{
                flex: 1, padding: '12px', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer',
                background: t.accentRed, color: '#fff', border: 'none',
              }}>
                {backupDone ? S().deletion.confirmDelete : S().deletion.deleteWithoutBackup}
              </button>
            </div>
          </>
        )}

        {/* Шаг 3 (Stage 11): подтверждение паролем — необратимое действие */}
        {step === 3 && (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Подтверждение</div>
            <div style={{ width: 48, height: 48, borderRadius: 14, background: `${t.accentRed}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Lock size={24} color={t.accentRed} />
            </div>
            <div style={{ fontSize: 17, fontWeight: 800, color: t.text, textAlign: 'center', marginBottom: 10 }}>
              Введите пароль для подтверждения
            </div>
            <div style={{ fontSize: 13, color: t.textSec, textAlign: 'center', lineHeight: 1.5, marginBottom: 18 }}>
              Это действие необратимо. Подтвердите его текущим паролем аккаунта.
            </div>
            <input
              type="password"
              value={password}
              onChange={e => { setPassword(e.target.value); setPwError(''); }}
              placeholder="Текущий пароль"
              autoFocus
              autoComplete="current-password"
              onKeyDown={e => { if (e.key === 'Enter' && password) handleDelete(); }}
              style={{ width: '100%', padding: '12px 14px', fontSize: 15, borderRadius: 10, border: `1px solid ${t.border}`, background: t.bgInput, color: t.text, marginBottom: pwError ? 6 : 16 }}
            />
            {pwError && (
              <div style={{ fontSize: 12, color: t.accentRed, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertCircle size={12} /> {pwError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={onClose} disabled={deleting} style={{ flex: 1, padding: '12px', borderRadius: 10, background: t.bgInput, color: t.textSec, fontSize: 14, fontWeight: 600, cursor: deleting ? 'wait' : 'pointer', opacity: deleting ? 0.6 : 1 }}>
                {S().common.cancel}
              </button>
              <button onClick={handleDelete} disabled={deleting || !password} style={{
                flex: 1, padding: '12px', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: deleting || !password ? 'not-allowed' : 'pointer',
                background: t.accentRed, color: '#fff', opacity: (deleting || !password) ? 0.6 : 1, border: 'none',
              }}>
                {deleting ? S().deletion.deleting : 'Удалить навсегда'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function WelcomeTour({ onClose, onNavigate }) {
  const t = useTheme();
  const [step, setStep] = useState(0);
  const totalSteps = 5; // welcome + 4 features

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' || e.key === 'Enter') { if (step < totalSteps - 1) setStep(step + 1); else onClose(); }
      if (e.key === 'ArrowLeft' && step > 0) setStep(step - 1);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [step, onClose]);

  const steps = [
    { icon: '🎉', title: S().tour.welcome, desc: S().tour.welcomeDesc, color: t.accent },
    { icon: '💳', title: S().tour.step1Title, desc: S().tour.step1Desc, color: '#5b9cf6', tab: 'af' },
    { icon: '📚', title: S().tour.step2Title, desc: S().tour.step2Desc, color: '#f06caa', tab: 'data' },
    { icon: '⚡', title: S().tour.step3Title, desc: S().tour.step3Desc, color: '#f0a040', tab: 'ops' },
    { icon: '📊', title: S().tour.step4Title, desc: S().tour.step4Desc, color: '#34c77b', tab: 'reports' },
  ];

  const cur = steps[step];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.65)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, backdropFilter: 'blur(4px)' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: t.bgCard,
        border: `2px solid ${cur.color}40`,
        borderRadius: 20,
        padding: '36px 32px 28px',
        width: 440,
        maxWidth: '95vw',
        boxShadow: `0 24px 80px rgba(0,0,0,.4), 0 0 0 1px ${cur.color}15`,
        animation: 'helpTooltipIn 0.3s cubic-bezier(0.16,1,0.3,1) forwards',
        textAlign: 'center',
        position: 'relative',
      }}>
        {/* Skip button */}
        <button onClick={onClose} style={{ position: 'absolute', top: 12, right: 16, background: 'transparent', color: t.textMuted, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', opacity: 0.7, transition: 'opacity 0.2s' }} onMouseEnter={e => e.currentTarget.style.opacity = 1} onMouseLeave={e => e.currentTarget.style.opacity = 0.7}>
          {S().tour.skipBtn} ✕
        </button>

        {/* Icon */}
        <div style={{ fontSize: 48, marginBottom: 16, lineHeight: 1 }}>{cur.icon}</div>

        {/* Title */}
        <div style={{ fontSize: 22, fontWeight: 800, color: t.text, marginBottom: 10 }}>{cur.title}</div>

        {/* Description */}
        <div style={{ fontSize: 14, color: t.textSec, lineHeight: 1.7, marginBottom: 24, maxWidth: 360, margin: '0 auto 24px' }}>{cur.desc}</div>

        {/* Progress dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 20 }}>
          {steps.map((_, i) => (
            <div key={i} onClick={() => setStep(i)} style={{
              width: i === step ? 24 : 8,
              height: 8,
              borderRadius: 4,
              background: i === step ? cur.color : `${t.textMuted}40`,
              transition: 'all 0.3s ease',
              cursor: 'pointer',
            }} />
          ))}
        </div>

        {/* Navigation buttons */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
          {step > 0 && (
            <button onClick={() => setStep(step - 1)} style={{
              padding: '10px 20px', borderRadius: 10, background: t.bgInput,
              color: t.textSec, fontWeight: 700, fontSize: 13, border: `1px solid ${t.border}`,
              cursor: 'pointer', transition: 'all 0.2s',
            }}>{S().tour.prevBtn}</button>
          )}
          <button onClick={() => {
            if (step < totalSteps - 1) setStep(step + 1);
            else onClose();
          }} style={{
            padding: '10px 28px', borderRadius: 10, background: cur.color,
            color: '#fff', fontWeight: 700, fontSize: 14, border: 'none',
            cursor: 'pointer', transition: 'all 0.2s',
            boxShadow: `0 4px 16px ${cur.color}40`,
          }}>
            {step === totalSteps - 1 ? S().tour.finishBtn : S().tour.nextBtn}
          </button>
        </div>

        {/* Step counter */}
        <div style={{ marginTop: 16, fontSize: 11, color: t.textMuted }}>{S().tour.stepOf(step + 1, totalSteps)}</div>
      </div>
    </div>
  );
}

// ════════════ ЮРИДИЧЕСКИЕ ДОКУМЕНТЫ (РАСШИРЕННЫЕ) ════════════
export function LegalDocsModal({ doc, onClose }) {
  const t = useTheme();
  let title = '';
  let content = null;

  const unselectableStyle = { userSelect: 'none', WebkitUserSelect: 'none', msUserSelect: 'none', MozUserSelect: 'none', fontSize: 13, lineHeight: 1.6, color: t.textSec, textAlign: 'justify' };
  const hStyle = { color: t.text, marginTop: 18, marginBottom: 8, fontSize: 14, fontWeight: 800 };
  const pStyle = { marginBottom: 10 };

  const linkStyle = { color: '#58a6ff', textDecoration: 'underline', cursor: 'pointer' };

  if (doc === 'tos') {
    title = S().legal.tosTitle;
    content = (
      <div style={unselectableStyle}>
        <div style={{ ...pStyle, padding: '12px 16px', background: 'rgba(88,166,255,0.08)', borderRadius: 8, border: '1px solid rgba(88,166,255,0.15)', marginBottom: 16 }}>
          <a href="/legal/terms.html" target="_blank" rel="noopener noreferrer" style={linkStyle}>Открыть полную версию документа &rarr;</a>
        </div>
        <div style={{ ...pStyle, fontSize: 12, color: t.textMuted, marginBottom: 16 }}>ИП «Елемесова Айдана Боранбайкызы» &middot; БИН/ИИН: 920207451098 &middot; г. Астана &middot; Редакция от 13.04.2026</div>

        <h4 style={hStyle}>1. Термины и определения</h4>
        <div style={pStyle}>Сервис — программно-аппаратный комплекс INMONEX (https://inmonex.kz). Администрация — ИП «Елемесова Айдана Боранбайкызы». Пользователь — лицо, прошедшее регистрацию. Организация — виртуальная единица учёта. Подписка — платный период расширенного доступа.</div>

        <h4 style={hStyle}>2. Общие положения</h4>
        <div style={pStyle}>Настоящий документ является публичной офертой (ст. 395 ГК РК). Регистрация = акцепт оферты. Сервис — инструмент визуализации финансовых данных, не бухгалтерская / юридическая / финансовая консультация.</div>

        <h4 style={hStyle}>3. Ответственность</h4>
        <div style={pStyle}>Сервис предоставляется «Как есть». Администрация не несёт ответственности за убытки, упущенную выгоду, налоговые штрафы. Достоверность данных — ответственность Пользователя. Совокупная ответственность ограничена суммой оплаты за последние 3 месяца.</div>

        <h4 style={hStyle}>4. Оплата и возврат</h4>
        <div style={pStyle}>100% предоплата за выбранный период (1/6/12/24 мес). Цены в тенге (KZT). Возврат за активированный период не производится. Ошибочные (двойные) платежи возвращаются по заявлению в течение 14 дней.</div>

        <h4 style={hStyle}>5. Реферальная программа</h4>
        <div style={pStyle}>Бонус 10% от первого платежа приглашённого (подписка от 6 мес). Бонусы используются для оплаты подписки или передачи другим пользователям. Вывод в деньги невозможен.</div>

        <h4 style={hStyle}>6. Споры</h4>
        <div style={pStyle}>Переговоры → суд г. Астана (РК). Изменения с уведомлением за 10 дней. Применимое право — законодательство Республики Казахстан.</div>
      </div>
    );
  } else if (doc === 'privacy') {
    title = S().legal.privacyTitle;
    content = (
      <div style={unselectableStyle}>
        <div style={{ ...pStyle, padding: '12px 16px', background: 'rgba(88,166,255,0.08)', borderRadius: 8, border: '1px solid rgba(88,166,255,0.15)', marginBottom: 16 }}>
          <a href="/legal/privacy.html" target="_blank" rel="noopener noreferrer" style={linkStyle}>Открыть полную версию документа &rarr;</a>
        </div>
        <div style={{ ...pStyle, fontSize: 12, color: t.textMuted, marginBottom: 16 }}>Оператор ПДн: ИП «Елемесова Айдана Боранбайкызы» &middot; БИН/ИИН: 920207451098 &middot; г. Астана &middot; Редакция от 13.04.2026</div>

        <h4 style={hStyle}>Собираемые данные</h4>
        <div style={pStyle}>Идентификационные (логин, email, IP), данные аутентификации (хеш пароля), коммерческие данные (контрагенты, операции, балансы — вводятся вами), данные об использовании (тариф, подписка, бонусы), платёжные (факт оплаты, сумма — данные карт не хранятся на наших серверах).</div>

        <h4 style={hStyle}>Хранение данных</h4>
        <div style={pStyle}>Все базы данных физически расположены на серверах в Республике Казахстан (регион: Астана) в соответствии с Законом РК «О персональных данных и их защите».</div>

        <h4 style={hStyle}>Передача третьим лицам</h4>
        <div style={pStyle}>Данные не продаются и не передаются рекламным агентствам. Передача только: платёжным системам (для проведения оплаты), хостинг-провайдеру (серверы в РК), сервису email-рассылки (транзакционные уведомления).</div>

        <h4 style={hStyle}>Ваши права</h4>
        <div style={pStyle}>Доступ к своим данным, исправление, удаление, отзыв согласия, экспорт — через support@inmonex.kz (ответ до 15 рабочих дней). Защита: HTTPS, bcrypt, SSH-доступ, бэкапы.</div>
      </div>
    );
  } else if (doc === 'consent') {
    title = S().legal.consentTitle;
    content = (
      <div style={unselectableStyle}>
        <div style={{ ...pStyle, padding: '12px 16px', background: 'rgba(88,166,255,0.08)', borderRadius: 8, border: '1px solid rgba(88,166,255,0.15)', marginBottom: 16 }}>
          <a href="/legal/consent.html" target="_blank" rel="noopener noreferrer" style={linkStyle}>Открыть полную версию документа &rarr;</a>
        </div>
        <div style={{ ...pStyle, fontSize: 12, color: t.textMuted, marginBottom: 16 }}>Оператор: ИП «Елемесова Айдана Боранбайкызы» &middot; БИН/ИИН: 920207451098 &middot; г. Астана &middot; Редакция от 13.04.2026</div>

        <h4 style={hStyle}>1. Формирование согласия</h4>
        <div style={pStyle}>В соответствии с Законом РК от 21.05.2013 № 94-V «О персональных данных и их защите», регистрация в INMONEX с отметкой чекбокса является выражением свободного и осознанного согласия на обработку персональных данных.</div>

        <h4 style={hStyle}>2. Действия с данными</h4>
        <div style={pStyle}>Сбор, запись, систематизация, накопление, хранение, уточнение, извлечение, использование, передача, обезличивание, блокирование, удаление, уничтожение — автоматизированным способом.</div>

        <h4 style={hStyle}>3. Третьи лица</h4>
        <div style={pStyle}>Платёжные системы (для оплаты), хостинг-провайдер (серверы в РК), сервис email (транзакционные письма). Иная передача — только с дополнительного согласия или по закону РК.</div>

        <h4 style={hStyle}>4. Отзыв</h4>
        <div style={pStyle}>Согласие действует бессрочно. Отзыв — письменное заявление на support@inmonex.kz. Влечёт необратимое удаление аккаунта и всех данных. Обработка до 15 рабочих дней.</div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 16, width: 700, maxWidth: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,.4)' }}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${t.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 800 }}>{title}</div>
          <button onClick={onClose} style={{ background:'#fee2e2', border:'2px solid #b91c1c', borderRadius:10, padding:5, color:'#b91c1c', cursor:'pointer' }}><X size={18} /></button>
        </div>
        <div style={{ padding: '20px 24px', overflowY: 'auto', userSelect: 'none' }}>
          {content}
        </div>
        <div style={{ padding: '16px 24px', borderTop: `1px solid ${t.border}`, textAlign: 'right', background: t.bgInput, borderBottomLeftRadius: 16, borderBottomRightRadius: 16 }}>
          <Btn variant="primary" onClick={onClose} style={{ padding: '10px 24px' }}>{S().legal.agreeBtn}</Btn>
        </div>
      </div>
    </div>
  );
}
   